/**
 * TTPChatWidget - Pre-built UI widget for text chat using TextChatSDK
 * Similar to AgentWidget but designed for text-based chat
 */

import TextChatSDK from '../core/TextChatSDK.js';
import { VoiceInterface } from './VoiceInterface.js';
import { TextInterface } from './TextInterface.js';
import widgetTranslations from './widget-translations.js';
import { loadFlavor } from '../flavors/index.js';
import { EcommerceVoiceStrip } from '../ecommerce/EcommerceVoiceStrip.js';

/** Fallback for `buildWebSocketUrl()` when no optional `websocketUrl` override is set (VoiceSDK v2 uses the same default). */
const DEFAULT_VOICE_WEBSOCKET_URL = 'wss://speech.talktopc.com/ws/conv';

function isNonEmptyConfigString(v) {
  return v != null && String(v).trim() !== '';
}

/** True when the integrator set voice start-call colors (not relying on SDK defaults). */
function isVoiceStartCallAppearanceCustomized(rawConfig) {
  const v = rawConfig?.voice;
  if (!v || typeof v !== 'object') return false;
  const set = (k) => Object.prototype.hasOwnProperty.call(v, k) && isNonEmptyConfigString(v[k]);
  return set('primaryBtnGradient1') || set('primaryBtnGradient2') || set('startCallButtonColor');
}

function computeIsLightPanel(panelBg) {
  if (!panelBg || panelBg.charAt(0) !== '#') return false;
  const r = parseInt(panelBg.substr(1, 2), 16);
  const g = parseInt(panelBg.substr(3, 2), 16);
  const b = parseInt(panelBg.substr(5, 2), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

/**
 * Solid CSS color for "TalkToPC" footer link: brand purple by default; matches start-call accent when customized.
 */
/** First solid #rrggbb in a color string (hex or CSS containing hex). */
function parseSolidHexFromColorString(c) {
  if (c == null || typeof c !== 'string') return null;
  const t = c.trim();
  if (t.startsWith('#')) {
    if (t.length === 4) {
      const r = t[1];
      const g = t[2];
      const b = t[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (t.length === 7) return t;
    return null;
  }
  const m = t.match(/#[0-9a-fA-F]{6}/i);
  return m ? m[0] : null;
}

/** Accent for text chat (send, focus ring) from the same palette as the Start Call / voice primary buttons. */
function extractTextChatThemeAccent(voice) {
  const v = voice && typeof voice === 'object' ? voice : {};
  const withStartFallback = {
    primaryBtnGradient1: v.primaryBtnGradient1,
    primaryBtnGradient2: v.primaryBtnGradient2,
    startCallButtonColor: v.startCallButtonColor || '#667eea',
  };
  const candidates = [
    withStartFallback.primaryBtnGradient1,
    withStartFallback.primaryBtnGradient2,
    withStartFallback.startCallButtonColor,
  ];
  for (const c of candidates) {
    const hex = parseSolidHexFromColorString(c);
    if (hex) return hex;
  }
  return '#7C3AED';
}

function shadeHexColor(hex, factor) {
  const h = String(hex || '').replace('#', '');
  if (h.length !== 6) return '#6D28D9';
  const r = Math.max(0, Math.min(255, Math.round(parseInt(h.slice(0, 2), 16) * factor)));
  const g = Math.max(0, Math.min(255, Math.round(parseInt(h.slice(2, 4), 16) * factor)));
  const b = Math.max(0, Math.min(255, Math.round(parseInt(h.slice(4, 6), 16) * factor)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

function talkToPcLinkColorForPanel(isLightPanel, mergedVoice, rawConfig, voiceUiEnabled) {
  const startAccentDark =
    mergedVoice.primaryBtnGradient1
    || mergedVoice.primaryBtnGradient2
    || mergedVoice.startCallButtonColor
    || '#6d56f5';
  const defaultDark = startAccentDark;
  const defaultLight = '#7C3AED';
  const defaults = isLightPanel ? defaultLight : defaultDark;
  if (!voiceUiEnabled || !isVoiceStartCallAppearanceCustomized(rawConfig)) return defaults;
  const raw =
    mergedVoice.primaryBtnGradient1
    || mergedVoice.primaryBtnGradient2
    || mergedVoice.startCallButtonColor;
  if (!isNonEmptyConfigString(raw)) return defaults;
  const t = String(raw).trim();
  if (t.startsWith('#')) {
    if (t.length === 4) {
      const r = t[1];
      const g = t[2];
      const b = t[3];
      return `#${r}${r}${g}${g}${b}${b}`;
    }
    if (t.length === 7) return t;
  }
  const m = t.match(/#[0-9a-fA-F]{6}/i);
  if (m) return m[0];
  return defaults;
}

export class TTPChatWidget {
  constructor(config = {}) {
    this._rawConfig = config;
    // Merge user config with defaults (backward compatible with simple config)
    this.config = this.mergeWithDefaults(config);
    // Silently ignore allowOverride (now server-side only)
    
    // Shared client-tool handler map. Both VoiceSDK and TextChatSDK look up
    // handlers in this Map, so a single registerToolHandler() call works for
    // whichever channel the LLM happens to be running on at tool-call time.
    this._sharedToolHandlers = new Map();
    this.sdk = new TextChatSDK({ ...this.config, sharedToolHandlers: this._sharedToolHandlers });
    this.isOpen = false;
    this.isActive = false;
    this.translations = widgetTranslations;
    // Track current view state in unified mode to prevent resetting when widget is recreated
    this.currentView = 'voice'; // 'text' or 'voice' (unified home is always voice hero)
    // Race condition prevention: prevent multiple rapid clicks on Voice Call button
    this.isStartingCall = false;
    // Shadow DOM root for CSS isolation
    this.shadowRoot = null;
    // Container for widget elements (shadow root or regular DOM)
    this.container = null;
    
    // Create shadow host and root first (before interfaces, so we can pass shadowRoot to them)
    if (this.config.useShadowDOM) {
      this.createShadowHost();
      this.container = this.shadowRoot;
    } else {
      // No Shadow DOM - create regular container and append to body
      this.createRegularContainer();
      this.container = this.regularContainer;
      this.shadowRoot = document; // For querySelector/getElementById compatibility
    }
    
    // Initialize interfaces with proper config (after shadow root is created)
    // Voice interface needs voice config merged with main config
    const widgetModeForVoice = this.config.behavior?.mode || 'unified';
    const showVoiceUi = widgetModeForVoice === 'unified' || widgetModeForVoice === 'voice-only';
    const panelBgForBrand = this.config.panel?.backgroundColor || '#16161e';
    const talkToPcLink = talkToPcLinkColorForPanel(
      computeIsLightPanel(panelBgForBrand),
      this.config.voice,
      this._rawConfig,
      showVoiceUi
    );
    const desktopMinimizedEcommerceStripUi =
      typeof window !== 'undefined' &&
      window.innerWidth > 768 &&
      this.config.flavor?.callView === 'minimized';
    const voiceConfig = {
      ...this.config,
      ...this.config.voice,
      agentName: this.config.agentName,
      _talkToPcLinkColor: talkToPcLink,
      // Pass through text input placeholder for voice text input
      inputPlaceholder: this.config.text?.inputPlaceholder || this.config.inputPlaceholder || 'Type your message...',
      // Pass through send button color for voice send button
      sendButtonColor: this.config.text?.sendButtonColor || this.config.sendButtonColor || '#7C3AED',
      language: this.config.voice?.language || this.config.language || 'en',
      websocketUrl: this.config.websocketUrl,
      agentSettingsOverride: this.config.agentSettingsOverride,
      visualAssistant: this.config.visualAssistant,
      flavor: this.config.flavor || null,
      translations: this.translations,
      shadowRoot: this.shadowRoot,
      textSDK: this.sdk,
      sharedToolHandlers: this._sharedToolHandlers,
      inputFormat: this.config.inputFormat,
      // Output format defaults for widget: 24 kHz, 16-bit, PCM, raw
      outputContainer: this.config.voice?.outputContainer || 'raw',
      outputEncoding: this.config.voice?.outputEncoding || 'pcm',
      outputSampleRate: this.config.voice?.outputSampleRate || 24000,
      outputChannels: this.config.voice?.outputChannels || 1,
      outputBitDepth: this.config.voice?.outputBitDepth || 16,
      desktopMinimizedEcommerceStripUi,
      onDesktopMinimizedStripCallStart: desktopMinimizedEcommerceStripUi
        ? () => {
            this._ecommerceVoiceStrip?.show();
            if (this.isOpen) this.minimize();
            this._setPanelFooterVisible(false);
            this._setEcommerceStripLauncherVisible(false);
          }
        : undefined,
      onDesktopMinimizedStripLauncherRestore: desktopMinimizedEcommerceStripUi
        ? () => this._restoreDesktopEcommerceStripChrome()
        : undefined,
      // Pass event callbacks to voice interface (forward only; panel is not auto-minimized on call start)
      onConversationStart: (...args) => {
        if (typeof this.config.onConversationStart === 'function') {
          this.config.onConversationStart(...args);
        }
      },
      onConversationEnd: this.config.onConversationEnd,
      onBargeIn: this.config.onBargeIn,
      onAudioStartPlaying: this.config.onAudioStartPlaying,
      onAudioStoppedPlaying: this.config.onAudioStoppedPlaying,
      onSubtitleDisplay: this.config.onSubtitleDisplay,
      onVoiceCallButtonClick: this.config.onVoiceCallButtonClick,
      // Callback when call ends — return to unified home (voice hero), not legacy mode picker
      // BUT: Don't switch views if we're showing a domain error
      onCallEnd: () => {
        if (this._isDomainErrorVisible()) {
          console.log('🚫 Domain error is displayed - NOT returning to home from onCallEnd');
          return;
        }
        
        const widgetMode = this.config.behavior?.mode || 'unified';
        if (widgetMode === 'unified') {
          if (window.innerWidth <= 768) {
            // Mobile: close panel and show the FAB so user returns to normal state
            const panel = this.shadowRoot?.getElementById('text-chat-panel');
            if (panel) {
              panel.classList.remove('open');
              this.isOpen = false;
            }
            const fab = this.shadowRoot?.querySelector('.ttp-mobile-fab');
            if (fab) fab.style.display = '';
          }
          this.showLanding();
        }
      },
      // Callback to ensure voice interface is visible (used for domain errors)
      onShowVoice: () => {
        this.showVoice();
      },
      // Callback to show unified home (voice hero; used by domain error back button)
      onShowLanding: () => {
        this.showLanding();
      },
      // User declined server disclaimer before mic: return mobile Call/Chat overlay (not stuck on in-panel Start Call)
      onDisclaimerDeclined: () => {
        if (this._isDomainErrorVisible()) return;
        const widgetMode = this.config.behavior?.mode || 'unified';
        // Voice-only: end call UX is idle voice view (WebSocket already disconnected in VoiceInterface).
        if (widgetMode === 'voice-only') {
          this.showVoice();
          return;
        }
        if (widgetMode !== 'unified') return;
        if (window.innerWidth <= 768) {
          const panel = this.shadowRoot?.getElementById('text-chat-panel');
          if (panel) {
            panel.classList.remove('open');
            this.isOpen = false;
          }
          this._openMobileCallTextLanding();
        } else {
          this.showLanding();
        }
      },
      // Callback to show error toast (for domain errors, etc.)
      onErrorToast: (message, type) => {
        this.showErrorToast(message, type);
      },
      onMobileError: (message) => {
        this.showMobileLandingError(message);
      },
      // Root layout direction — must be last so `...this.config.voice` cannot overwrite with `undefined`
      direction: this.getEffectiveTextDirection(),
    };
    this.voiceInterface = new VoiceInterface(voiceConfig);

    // Universal widget-control listener — backend pushes {t:'minimize_widget'}
    // / {t:'maximize_widget'} on either channel (voice or text-chat) to
    // collapse / expand the panel. Wired UNCONDITIONALLY of any flavor so a
    // partner-driven UX choreography (e.g. Ocado opening the native trolley
    // drawer → minimize the widget) works regardless of how the host
    // configured this widget. Idempotent on both ends.
    this._wireWidgetControlListeners();

    const textConfig = {
      ...this.config,
      ...this.config.text,
      translations: this.translations,
      shadowRoot: this.shadowRoot // Pass shadow root reference
    };
    this.textInterface = new TextInterface(textConfig, this.sdk);
    this.textInterface._widget = this; // Reference for gallery viewer in inline media
    
    // Now create widget HTML and populate shadow root
    this.createWidget();
    
    this.setupEventHandlers();
    
    // Start open if configured
    if (this.config.behavior.startOpen || this.config.behavior.autoOpen) {
      if (window.innerWidth <= 768) {
        // Mobile: show the mobile landing overlay instead of the desktop panel
        const mobileFab = this.shadowRoot?.querySelector('.ttp-mobile-fab');
        const mobileLanding = this.shadowRoot?.getElementById('ttpMobileLanding');
        if (mobileFab && mobileLanding) {
          mobileFab.style.display = 'none';
          mobileLanding.classList.add('active');
          this.isMobileLandingOpen = true;
        }
      } else {
        const panel = this.shadowRoot?.getElementById('text-chat-panel');
        if (panel) {
          panel.style.removeProperty('display');
          this.isOpen = true;
          panel.classList.add('open');
        }
      }
    }
    
    // Check for resume state and auto-resume if needed
    this.checkAndHandleResume();
    
    // Initialize flavor module if configured (use merged config so flavor matches this.config)
    this._flavor = null;
    this._flavorObj = this.config.flavor && typeof this.config.flavor === 'object' ? this.config.flavor : null;
    if (this._flavorObj && this._flavorObj.type) {
      this._initFlavor(this._flavorObj.type);
      this._initEcommerceVoiceStrip();
    } else if (this._flavorObj && !this._flavorObj.type) {
      console.error('[TTPChatWidget] flavor object is set but missing required "type" field. Widget will run in vanilla mode.');
    }
  }
  
  /**
   * Check for resume state and auto-resume call if needed
   */
  checkAndHandleResume() {
    const resumeInfo = this.voiceInterface.checkAndHandleResume();
    
    if (resumeInfo) {
      console.log('🔄 [WIDGET] Auto-resuming call from page navigation');
      console.log('   From:', resumeInfo.fromPage, '→ To:', resumeInfo.toPage);
      
      // On mobile, close the mobile landing overlay before resuming
      // (startOpen may have opened it, and the resume flow bypasses the normal button click path)
      if (window.innerWidth <= 768) {
        const mobileLanding = this.shadowRoot?.getElementById('ttpMobileLanding');
        const mobileFab = this.shadowRoot?.querySelector('.ttp-mobile-fab');
        if (mobileLanding) {
          mobileLanding.classList.remove('active');
          this.isMobileLandingOpen = false;
        }
        if (mobileFab) {
          mobileFab.style.display = 'none';
        }
      }
      
      // Auto-open widget
      this.open();
      
      // Switch to voice mode UI (skip landing screen, go directly to voice interface)
      this.showVoice();
      
      // Set currentView to 'voice' to prevent showing landing screen
      this.currentView = 'voice';
      
      // Auto-start voice call with resume
      // The conversationId is already stored in sessionStorage and will be sent via query param
      // Use a small delay to ensure widget is fully opened
      setTimeout(() => {
        this.startVoiceCall().then(() => {
          console.log('✅ [WIDGET] Auto-resumed call successfully');
        }).catch((error) => {
          console.error('❌ [WIDGET] Failed to auto-resume call:', error);
          // On error, could show landing screen
          const widgetMode = this.config.behavior?.mode || 'unified';
          if (widgetMode === 'unified') {
            this.showLanding();
          }
        });
      }, 100);
    }
  }
  
  /**
   * Initialize and wire up a flavor module.
   * Registers client tools, hooks WebSocket message handlers, and sets up call-end behavior.
   */
  _initFlavor(flavorName) {
    const factory = loadFlavor(flavorName);
    if (!factory) {
      console.error(`[TTPChatWidget] Unknown flavor: "${flavorName}". Available flavors: ecommerce, hotels, restaurants, tours, learner. Widget will run in vanilla mode.`);
      return;
    }

    if (flavorName === 'ecommerce' && !this._flavorObj?.partnerId) {
      console.error('[TTPChatWidget] flavor "ecommerce" requires partnerId (e.g. { type: "ecommerce", partnerId: "shopify", shopifyDomain: "..." }). Widget will run in vanilla mode.');
      return;
    }

    this._flavor = factory(this, this._flavorObj || {});

    // Register client tools
    if (this._flavor.tools) {
      this._flavor.tools.forEach(tool => {
        this.registerToolHandler(tool.name, tool.handler);
      });
    }

    // Register WebSocket message handlers via voice pipeline
    if (this._flavor.messageHandlers) {
      const agentSDK = this.voiceInterface?.sdk;
      if (agentSDK) {
        agentSDK.onUnsupportedAction = null;
        const flavor = this._flavor;
        const originalConnect = agentSDK.connect.bind(agentSDK);

        agentSDK.connect = async function () {
          await originalConnect();
          if (this.voiceSDK) {
            this.voiceSDK.on('message', (message) => {
              const type = message.t || message.type;
              if (flavor.messageHandlers[type]) {
                flavor.messageHandlers[type](message);
              }
            });

            this.voiceSDK.on('stopPlaying', () => {
              if (flavor.messageHandlers['dismiss_media']) {
                flavor.messageHandlers['dismiss_media']();
              }
            });

            this.voiceSDK.on('bargeIn', () => {
              if (flavor.messageHandlers['dismiss_media']) {
                flavor.messageHandlers['dismiss_media']();
              }
            });

            console.log(`[TTPChatWidget] Flavor "${flavorName}" message listeners attached (voice)`);
          }
        };
      }

      // Wire flavor message handlers to text chat SDK — but exclude messages that
      // render in the voice UI (show_media, dismiss_media, show_items, show_products).
      // These are either handled inline in the chat bubble or skipped for now.
      // TODO: render show_items inline in text chat (room cards, product cards)
      if (this.sdk) {
        const flavor = this._flavor;
        const TEXT_CHAT_EXCLUDED = new Set([
          'show_media', 'dismiss_media',  // Handled inline as image thumbnails
          'show_items', 'show_products',  // Render in voice UI — skip for text chat for now
        ]);
        this.sdk.on('message', (message) => {
          const type = message.t || message.type;
          if (!TEXT_CHAT_EXCLUDED.has(type) && flavor.messageHandlers[type]) {
            flavor.messageHandlers[type](message);
          }
        });
        console.log(`[TTPChatWidget] Flavor "${flavorName}" message listeners attached (text chat)`);
      }
    }

    // Hook call-end if flavor provides it
    if (this._flavor.hookCallEnd) {
      this._flavor.hookCallEnd();
    }
  }

  /**
   * Resolved direction for widget chrome (shadow host, launcher). After mergeWithDefaults,
   * config.direction is always 'ltr' or 'rtl'.
   */
  getEffectiveTextDirection() {
    return this.config?.direction === 'rtl' ? 'rtl' : 'ltr';
  }

  /**
   * Open the widget
   */
  open() {
    if (this.isOpen) return;
    
    const panel = this.shadowRoot?.getElementById('text-chat-panel');
    if (panel) {
      panel.style.removeProperty('display');
      this.isOpen = true;
      panel.classList.add('open');
    }
  }

  /**
   * Minimize the widget — collapse the chat panel back to the round launcher
   * pill, equivalent to the user clicking the launcher while the panel is
   * open. Does NOT end an in-flight voice call (call survives, the user
   * just sees the bubble until they re-open).
   *
   * Idempotent: no-op when the panel is already minimized. Safe to call
   * from a backend-pushed {t:'minimize_widget'} regardless of current state.
   */
  minimize() {
    if (!this.isOpen) return;
    this._doTogglePanel();
  }

  /**
   * Maximize the widget — expand the chat panel from the round launcher pill,
   * equivalent to the user clicking the launcher while the panel is closed.
   * Triggers the same auto-connect side-effect a real click would (when
   * configured), so a backend choreography that maximizes the widget at
   * call-start time still gets a connection.
   *
   * Idempotent: no-op when the panel is already open. Safe to call from a
   * backend-pushed {t:'maximize_widget'} regardless of current state.
   */
  maximize() {
    if (this.isOpen) return;
    this._doTogglePanel();
  }

  /**
   * DOM root that contains the launcher (`#text-chat-button-container`), matching createWidget.
   * @returns {ShadowRoot|Document}
   */
  _launcherDomRoot() {
    if (this.config.useShadowDOM !== false && this.shadowRoot && this.shadowRoot !== document) {
      return this.shadowRoot;
    }
    return document;
  }

  /**
   * Show or hide the floating launcher for desktop callView:minimized (bottom strip mode).
   * Widget CSS uses `display: flex !important` on the container — hide with `none !important`.
   * @param {boolean} visible
   */
  _setEcommerceStripLauncherVisible(visible) {
    if (this.config.behavior?.hidden) return;
    const root = this._launcherDomRoot();
    const container = root.getElementById('text-chat-button-container');
    const btn = root.getElementById('text-chat-button');
    if (visible) {
      if (container) container.style.removeProperty('display');
      if (btn) btn.style.removeProperty('display');
    } else if (container) {
      container.style.setProperty('display', 'none', 'important');
    } else if (btn) {
      btn.style.setProperty('display', 'none', 'important');
    }
  }

  /** Hide bottom strip and restore floating launcher (desktop minimized call UI). */
  _restoreDesktopEcommerceStripChrome() {
    this._ecommerceVoiceStrip?.hide();
    this._setPanelFooterVisible(true);
    this._setEcommerceStripLauncherVisible(true);
  }

  /**
   * Wire the universal widget-control message listener on both the
   * text-chat SDK and the voice SDK. Two channels, one set of message types
   * ({@code minimize_widget}, {@code maximize_widget}) — whichever channel
   * the backend pushes on, the panel reacts.
   *
   * <p>Voice path is monkey-patched onto {@code agentSDK.connect} (mirroring
   * {@code _initFlavor}'s pattern) because {@code voiceSDK} only exists
   * after connect. Subsequent monkey-patches by flavor wiring chain on top
   * of this one, both originals are preserved.</p>
   *
   * <p>This method is intentionally flavor-independent — runs even on
   * vanilla widgets so partners that drive widget UX from the backend (e.g.
   * Ocado: cart.show → minimize, cart.hide → maximize) work regardless of
   * how the widget was configured.</p>
   *
   * @private
   */
  _wireWidgetControlListeners() {
    const handle = (message) => {
      const type = message?.t || message?.type;
      if (type === 'minimize_widget') {
        this.minimize();
      } else if (type === 'maximize_widget') {
        this.maximize();
      }
    };

    // Text-chat path — TextChatSDK already exposes 'message' events on
    // every server frame. Attach immediately, no monkey-patch needed.
    if (this.sdk && typeof this.sdk.on === 'function') {
      this.sdk.on('message', handle);
    }

    // Voice path — voiceSDK is born inside agentSDK.connect(). Mirror the
    // _initFlavor monkey-patch idiom so the listener attaches the moment
    // voiceSDK becomes available. Chained-safe with future patches.
    const agentSDK = this.voiceInterface?.sdk;
    if (agentSDK && typeof agentSDK.connect === 'function') {
      const originalConnect = agentSDK.connect.bind(agentSDK);
      agentSDK.connect = async function () {
        await originalConnect();
        if (this.voiceSDK && typeof this.voiceSDK.on === 'function') {
          this.voiceSDK.on('message', handle);
        }
      };
    }
  }

  /**
   * Merge user configuration with defaults
   * Supports both simple config (backward compatible) and advanced config
   */
  mergeWithDefaults(userConfig) {
    // Handle legacy position string format
    let positionConfig = userConfig.position || 'bottom-right';
    if (typeof positionConfig === 'string') {
      // Convert legacy format 'bottom-right' to new format
      const parts = positionConfig.split('-');
      positionConfig = {
        vertical: parts[0] || 'bottom',
        horizontal: parts[1] || 'right',
        offset: userConfig.positionOffset || { x: 20, y: 20 }
      };
    }

    // Handle legacy primaryColor - default to white (for floating button)
    const primaryColor = userConfig.primaryColor || userConfig.button?.primaryColor || userConfig.button?.backgroundColor || '#FFFFFF';
    
    // FIX: Don't calculate headerColor here - it causes buttons to inherit header color
    // Instead, use explicit defaults for landing screen colors that don't depend on header
    // Filter out empty strings from userConfig.landing to prevent overriding defaults
    const cleanLandingConfig = {};
    if (userConfig.landing) {
      Object.keys(userConfig.landing).forEach(key => {
        const value = userConfig.landing[key];
        // Only include non-empty values (skip empty strings, null, undefined)
        if (value !== null && value !== undefined && value !== '') {
          cleanLandingConfig[key] = value;
        }
      });
    }
    
    // Filter out empty strings from userConfig.header to prevent overriding defaults
    const cleanHeaderConfig = {};
    if (userConfig.header) {
      Object.keys(userConfig.header).forEach(key => {
        const value = userConfig.header[key];
        // Only include non-empty values (skip empty strings, null, undefined)
        if (value !== null && value !== undefined && value !== '') {
          cleanHeaderConfig[key] = value;
        }
      });
    }

    return {
      // Required (agentId is required, appId is optional)
      agentId: userConfig.agentId,
      appId: userConfig.appId,
      ...(userConfig.websocketUrl ? { websocketUrl: userConfig.websocketUrl } : {}),
      agentSettingsOverride: userConfig.agentSettingsOverride,
      demo: userConfig.demo !== false,
      direction: (() => {
        if (userConfig.direction === 'rtl' || userConfig.direction === 'ltr') {
          return userConfig.direction;
        }
        const langBase = String(userConfig.language || 'en').toLowerCase().split(/[-_]/)[0];
        if (langBase === 'he' || langBase === 'ar') return 'rtl';
        return 'ltr';
      })(),
      language: userConfig.language || 'en', // Optional - language for voice (defaults to 'en')
      // Agent display name (voice idle, mobile landing, flattened voice config) — root only; never under voice
      ...(isNonEmptyConfigString(userConfig.agentName)
        ? { agentName: String(userConfig.agentName).trim() }
        : {}),

      // Icon/Image Configuration
      icon: {
        type: userConfig.icon?.type || 'custom', // 'microphone', 'custom', 'emoji', 'text'
        emoji: userConfig.icon?.emoji || '🎤',
        text: userConfig.icon?.text || 'AI',
        size: userConfig.icon?.size || 'medium', // 'small', 'medium', 'large', 'xl'
        backgroundColor: userConfig.icon?.backgroundColor || '#FFFFFF',
        ...userConfig.icon,
        /* After spread: empty / omitted URL → null so desktop pill uses waveform like mobile FAB */
        customImage: (() => {
          const v = userConfig.icon?.customImage;
          if (v == null || String(v).trim() === '') return null;
          return String(v).trim();
        })()
      },
      
      // Positioning Configuration (supports both object and legacy string)
      position: {
        vertical: positionConfig.vertical || 'bottom',
        horizontal: positionConfig.horizontal || 'right',
        offset: positionConfig.offset || { x: 20, y: 20 },
        ...(typeof userConfig.position === 'object' ? userConfig.position : {})
      },
      
      // Button Configuration
      button: {
        size: userConfig.button?.size || 'medium',
        shape: userConfig.button?.shape || 'circle',
        // Floating button colors (main button) - default to white
        backgroundColor: userConfig.button?.backgroundColor || userConfig.icon?.backgroundColor || '#FFFFFF',
        hoverColor: userConfig.button?.hoverColor || '#D3D3D3', // Light gray default
        shadow: userConfig.button?.shadow !== false,
        shadowColor: userConfig.button?.shadowColor || 'rgba(0,0,0,0.15)',
        // Legacy support - map to backgroundColor
        primaryColor: userConfig.button?.backgroundColor || userConfig.icon?.backgroundColor || '#FFFFFF',
        ...userConfig.button
      },
      
      // Panel Configuration (common to both voice and text)
      panel: {
        width: userConfig.panel?.width || 360,
        height: userConfig.panel?.height || 500,
        borderRadius: userConfig.panel?.borderRadius || 24,
        backgroundColor: userConfig.panel?.backgroundColor || '#FFFFFF',
        backdropFilter: userConfig.panel?.backdropFilter || null,
        border: userConfig.panel?.border || '1px solid #E5E7EB', // Default gray border, fully customizable
        ...userConfig.panel
      },
      
      // Voice-specific Configuration (`voice.agentName` is ignored — use root `agentName`)
      voice: (() => {
        const voiceOut = {
          // Voice button colors (inside panel)
          micButtonColor: userConfig.voice?.micButtonColor || userConfig.panel?.micButtonColor || primaryColor,
          micButtonActiveColor: userConfig.voice?.micButtonActiveColor || userConfig.panel?.micButtonActiveColor || '#EF4444',
          // Voice button hint text (below button)
          micButtonHint: {
            text: userConfig.voice?.micButtonHint?.text || userConfig.panel?.micButtonHint?.text || 'Click the button to start voice conversation',
            color: userConfig.voice?.micButtonHint?.color || userConfig.panel?.micButtonHint?.color || '#6B7280',
            fontSize: userConfig.voice?.micButtonHint?.fontSize || userConfig.panel?.micButtonHint?.fontSize || '12px',
            ...userConfig.voice?.micButtonHint,
            ...userConfig.panel?.micButtonHint
          },
          // Voice interface colors
          avatarBackgroundColor: userConfig.voice?.avatarBackgroundColor || '#667eea',
          avatarActiveBackgroundColor: userConfig.voice?.avatarActiveBackgroundColor || '#667eea',
          statusTitleColor: userConfig.voice?.statusTitleColor || '#1e293b',
          statusSubtitleColor: userConfig.voice?.statusSubtitleColor || '#64748b',
          statusDotColor: userConfig.voice?.statusDotColor || '#10b981',
          statusText: userConfig.voice?.statusText || null,
          startCallButtonColor: userConfig.voice?.startCallButtonColor || '#667eea',
          startCallButtonTextColor: userConfig.voice?.startCallButtonTextColor || '#FFFFFF',
          startCallTitle: userConfig.voice?.startCallTitle || null,
          startCallSubtitle: userConfig.voice?.startCallSubtitle || null,
          startCallButtonText: userConfig.voice?.startCallButtonText || null,
          transcriptBackgroundColor: userConfig.voice?.transcriptBackgroundColor || '#FFFFFF',
          transcriptTextColor: userConfig.voice?.transcriptTextColor || '#1e293b',
          transcriptLabelColor: userConfig.voice?.transcriptLabelColor || '#94a3b8',
          // Live user STT line prefix (e.g. "You: "). null = use translation userTranscriptPrefix; '' = no prefix
          userTranscriptPrefix: userConfig.voice?.userTranscriptPrefix ?? null,
          controlButtonColor: userConfig.voice?.controlButtonColor || '#FFFFFF',
          controlButtonSecondaryColor: userConfig.voice?.controlButtonSecondaryColor || '#64748b',
          endCallButtonColor: userConfig.voice?.endCallButtonColor || '#ef4444',
          // Voice language setting
          language: userConfig.voice?.language || userConfig.language || 'en',
          ...userConfig.voice,
          // Allow header avatar at top level (some embeds / APIs send it outside `voice`)
          ...(isNonEmptyConfigString(userConfig.headerAvatarImageUrl) &&
          !isNonEmptyConfigString(userConfig.voice?.headerAvatarImageUrl)
            ? { headerAvatarImageUrl: String(userConfig.headerAvatarImageUrl).trim() }
            : {}),
        };
        delete voiceOut.agentName;
        return voiceOut;
      })(),
      
      // Text-specific Configuration (accent defaults follow voice primary / start-call colors)
      ...(() => {
        const rawVoice = userConfig.voice || {};
        const textChatAccent = extractTextChatThemeAccent(rawVoice);
        const g2 = parseSolidHexFromColorString(rawVoice.primaryBtnGradient2);
        const textChatAccentHover =
          g2 && g2.toLowerCase() !== textChatAccent.toLowerCase()
            ? g2
            : shadeHexColor(textChatAccent, 0.88);
        return {
      text: {
        // Send button colors (inside panel)
        sendButtonColor: userConfig.text?.sendButtonColor || userConfig.panel?.sendButtonColor || textChatAccent,
        sendButtonHoverColor: userConfig.text?.sendButtonHoverColor || userConfig.panel?.sendButtonHoverColor || textChatAccentHover,
        sendButtonActiveColor: userConfig.text?.sendButtonActiveColor || userConfig.panel?.sendButtonActiveColor || textChatAccentHover,
        sendButtonText: userConfig.text?.sendButtonText || userConfig.panel?.sendButtonText || '➤',
        sendButtonTextColor: userConfig.text?.sendButtonTextColor || userConfig.panel?.sendButtonTextColor || '#FFFFFF',
        sendButtonFontSize: userConfig.text?.sendButtonFontSize || userConfig.panel?.sendButtonFontSize || '20px',
        sendButtonFontWeight: userConfig.text?.sendButtonFontWeight || userConfig.panel?.sendButtonFontWeight || '500',
        // Send button hint text (below button or near it)
        sendButtonHint: {
          text: userConfig.text?.sendButtonHint?.text || userConfig.panel?.sendButtonHint?.text || '',
          color: userConfig.text?.sendButtonHint?.color || userConfig.panel?.sendButtonHint?.color || '#6B7280',
          fontSize: userConfig.text?.sendButtonHint?.fontSize || userConfig.panel?.sendButtonHint?.fontSize || '14px',
          ...userConfig.text?.sendButtonHint,
          ...userConfig.panel?.sendButtonHint
        },
        // Input field configuration
        inputPlaceholder: userConfig.text?.inputPlaceholder || userConfig.panel?.inputPlaceholder || 'Type your message...',
        inputBorderColor: userConfig.text?.inputBorderColor || userConfig.panel?.inputBorderColor || '#E5E7EB',
        inputFocusColor: userConfig.text?.inputFocusColor || userConfig.panel?.inputFocusColor || textChatAccent,
        inputBackgroundColor: userConfig.text?.inputBackgroundColor || userConfig.panel?.inputBackgroundColor || '#FFFFFF',
        inputTextColor: userConfig.text?.inputTextColor || userConfig.panel?.inputTextColor || '#1F2937',
        inputFontSize: userConfig.text?.inputFontSize || userConfig.panel?.inputFontSize || '16px',
        inputBorderRadius: userConfig.text?.inputBorderRadius || userConfig.panel?.inputBorderRadius || 20,
        inputPadding: userConfig.text?.inputPadding || userConfig.panel?.inputPadding || '8px 16px',
        /** When true (default), text chat uses the same hero gradient, primary, and bubble treatment as the voice UI. Set false for a light, panel-solid transcript. */
        useVoiceTheme: userConfig.text?.useVoiceTheme !== false,
        ...userConfig.text
      },
        };
      })(),

      // WhatsApp Configuration (optional — set to show WhatsApp option in idle view)
      whatsapp: userConfig.whatsapp ? {
        number: (userConfig.whatsapp.number || '').replace(/[^0-9]/g, ''),
        text: userConfig.whatsapp.text || '',
      } : null,
      
      // Landing Screen Configuration (only for unified mode)
      // FIX: Use explicit defaults instead of headerColor to prevent buttons from inheriting header color
      landing: (() => {
        const cleanLandingConfig = {};
        if (userConfig.landing) {
          Object.keys(userConfig.landing).forEach(key => {
            const value = userConfig.landing[key];
            if (value !== null && value !== undefined && value !== '') {
              cleanLandingConfig[key] = value;
            } else if (value === false) {
              cleanLandingConfig[key] = value;
            }
          });
        }
        
        return {
        backgroundColor: cleanLandingConfig.backgroundColor || 'linear-gradient(180deg, #f8fafc 0%, #e0e7ff 100%)',
        logo: cleanLandingConfig.logo || '🤖',
        title: cleanLandingConfig.title || null, // null means use default translated text
        titleColor: cleanLandingConfig.titleColor || '#1e293b',
        modeCardBackgroundColor: cleanLandingConfig.modeCardBackgroundColor || '#FFFFFF',
        modeCardBorderColor: cleanLandingConfig.modeCardBorderColor || '#E2E8F0',
        modeCardHoverBorderColor: cleanLandingConfig.modeCardHoverBorderColor || '#7C3AED', // Fixed: Use explicit default instead of headerColor
        modeCardIconBackgroundColor: cleanLandingConfig.modeCardIconBackgroundColor || '#7C3AED', // Fixed: Use explicit default instead of headerColor
        modeCardTitleColor: cleanLandingConfig.modeCardTitleColor || '#111827',
        voiceCardIcon: cleanLandingConfig.voiceCardIcon || '🎤',
        textCardIcon: cleanLandingConfig.textCardIcon || '💬',
        voiceCardTitle: cleanLandingConfig.voiceCardTitle || null,
        textCardTitle: cleanLandingConfig.textCardTitle || null,
        callButtonText: cleanLandingConfig.callButtonText || null,
        chatButtonText: cleanLandingConfig.chatButtonText || null,
        statusText: cleanLandingConfig.statusText || null,
        ...cleanLandingConfig // Spread cleaned config (no empty strings)
        };
      })(),
      
      // Header Configuration (top of panel)
      header: (() => {
        // Filter out empty strings to prevent overriding defaults
        const cleanHeaderConfig = {};
        if (userConfig.header) {
          Object.keys(userConfig.header).forEach(key => {
            const value = userConfig.header[key];
            // For online indicator properties, allow empty strings (they might be intentionally set)
            // For other properties, only include non-empty values
            if (key.startsWith('onlineIndicator')) {
              if (value !== null && value !== undefined) {
                cleanHeaderConfig[key] = value;
              }
            } else {
              // Only include non-empty values (skip empty strings, null, undefined)
              if (value !== null && value !== undefined && value !== '') {
                cleanHeaderConfig[key] = value;
              }
            }
          });
        }
        
        const finalHeader = {
          title: cleanHeaderConfig.title || 'Chat Assistant',
          showTitle: cleanHeaderConfig.showTitle !== false,
          backgroundColor: cleanHeaderConfig.backgroundColor || '#7C3AED', // Default purple
          textColor: cleanHeaderConfig.textColor || '#FFFFFF',
          showCloseButton: cleanHeaderConfig.showCloseButton !== false,
          ...cleanHeaderConfig // Spread cleaned config (includes online indicator properties); mobileLabel only if set — FAB falls back to pillTitle
        };
        
        
        return finalHeader;
      })(),
      
      // Footer Configuration (TTP Branding - always shown by default)
      footer: {
        show: userConfig.footer?.show !== false, // Default true
        backgroundColor: userConfig.footer?.backgroundColor || '#f9fafb',
        textColor: userConfig.footer?.textColor || '#9ca3af',
        hoverColor: userConfig.footer?.hoverColor || '#7C3AED',
        // Brand shown in "Powered by" footer. 'talktopc' (default) or 'speacart'.
        brand: userConfig.footer?.brand === 'speacart' ? 'speacart' : 'talktopc',
        ...userConfig.footer
      },
      
      // Tooltips Configuration
      tooltips: {
        newChat: userConfig.tooltips?.newChat || null, // null means use default based on direction
        back: userConfig.tooltips?.back || null,
        close: userConfig.tooltips?.close || null,
        mute: userConfig.tooltips?.mute || null,
        speaker: userConfig.tooltips?.speaker || null,
        endCall: userConfig.tooltips?.endCall || null,
        ...userConfig.tooltips
      },
      
      // Messages Configuration
      messages: {
        userBackgroundColor: userConfig.messages?.userBackgroundColor || '#E5E7EB',
        agentBackgroundColor: userConfig.messages?.agentBackgroundColor || '#F3F4F6',
        systemBackgroundColor: userConfig.messages?.systemBackgroundColor || '#DCFCE7',
        errorBackgroundColor: userConfig.messages?.errorBackgroundColor || '#FEE2E2',
        textColor: userConfig.messages?.textColor || '#1F2937', // Fallback for backward compatibility
        userTextColor: userConfig.messages?.userTextColor || userConfig.messages?.textColor || '#1F2937',
        agentTextColor: userConfig.messages?.agentTextColor || userConfig.messages?.textColor || '#1F2937',
        userAvatarIcon: userConfig.messages?.userAvatarIcon || '👤',
        agentAvatarIcon: userConfig.messages?.agentAvatarIcon || '🤖',
        fontSize: userConfig.messages?.fontSize || '16px',
        borderRadius: userConfig.messages?.borderRadius || 16,
        ...userConfig.messages
      },
      
      // Animation Configuration
      animation: {
        enableHover: userConfig.animation?.enableHover !== false,
        enablePulse: userConfig.animation?.enablePulse !== false,
        enableSlide: userConfig.animation?.enableSlide !== false,
        duration: userConfig.animation?.duration || 0.3,
        ...userConfig.animation
      },
      
      // Prompt Animation Configuration
      promptAnimation: {
        enabled: userConfig.promptAnimation?.enabled === true, // Default false
        text: userConfig.promptAnimation?.text || 'Try me!',
        backgroundColor: userConfig.promptAnimation?.backgroundColor || 'linear-gradient(135deg, #7c3aed, #4f46e5)',
        textColor: userConfig.promptAnimation?.textColor || '#ffffff',
        animationType: userConfig.promptAnimation?.animationType || 'bounce', // 'bounce' | 'pulse' | 'float' | 'none'
        showShimmer: userConfig.promptAnimation?.showShimmer !== false, // Default true
        showPulseRings: userConfig.promptAnimation?.showPulseRings !== false, // Default true
        hideAfterClick: userConfig.promptAnimation?.hideAfterClick !== false, // Default true
        hideAfterSeconds: userConfig.promptAnimation?.hideAfterSeconds !== undefined ? userConfig.promptAnimation.hideAfterSeconds : null, // null = never
        position: userConfig.promptAnimation?.position || 'top', // 'top' | 'left' | 'right'
        ...userConfig.promptAnimation
      },
      
      // Behavior Configuration
      behavior: {
        autoOpen: userConfig.behavior?.autoOpen || false,
        startOpen: userConfig.behavior?.startOpen || false,
        hidden: userConfig.behavior?.hidden || false,
        autoConnect: userConfig.behavior?.autoConnect || false,
        showWelcomeMessage: userConfig.behavior?.showWelcomeMessage !== false,
        welcomeMessage: userConfig.behavior?.welcomeMessage || 'Hello! How can I help you today?',
        enableVoiceMode: userConfig.behavior?.enableVoiceMode !== undefined ? userConfig.behavior.enableVoiceMode : true,
        // Widget mode: 'unified' (voice hero + text chat in panel; mobile pre-chat overlay), 'voice-only', 'text-only'
        mode: userConfig.behavior?.mode || 'unified',
        ...userConfig.behavior
      },
      
      // Accessibility Configuration
      accessibility: {
        ariaLabel: userConfig.accessibility?.ariaLabel || 'Chat Assistant',
        ariaDescription: userConfig.accessibility?.ariaDescription || 'Click to open chat assistant',
        keyboardNavigation: userConfig.accessibility?.keyboardNavigation !== false,
        ...userConfig.accessibility
      },
      
      // Custom CSS
      customStyles: userConfig.customStyles || '',
      
      // Shadow DOM Configuration
      useShadowDOM: userConfig.useShadowDOM !== false, // Default true for backward compatibility
      
      // Variables for the agent
      variables: userConfig.variables || {},
      // TEST: allow forcing a conversationId for debugging
      forceConversationId: userConfig.forceConversationId,
      
      // Visual Assistant Configuration
      visualAssistant: userConfig.visualAssistant,

      // Flavor system — single object: { type, partnerId, shopifyDomain, ... }
      flavor: userConfig.flavor && typeof userConfig.flavor === 'object' ? userConfig.flavor : null,
      
      // Event callbacks/hooks
      onConversationStart: userConfig.onConversationStart,
      onConversationEnd: userConfig.onConversationEnd,
      onBargeIn: userConfig.onBargeIn,
      onAudioStartPlaying: userConfig.onAudioStartPlaying,
      onAudioStoppedPlaying: userConfig.onAudioStoppedPlaying,
      onSubtitleDisplay: userConfig.onSubtitleDisplay,
      onVoiceCallButtonClick: userConfig.onVoiceCallButtonClick,

      // Legacy support (for backward compatibility)
      primaryColor: primaryColor,
      // Keep position string for backward compatibility
      ...(typeof userConfig.position === 'string' ? { positionString: userConfig.position } : {})
    };
  }

  setupEventHandlers() {
    // Handle domain validation errors
    this.sdk.on('domainError', (error) => {
      this.textInterface.showError(error);
      this.textInterface.stopStreamingState();
    });

    this.sdk.on('error', (error) => {
      // Domain errors are handled by domainError event, skip here
      if (error && (error.message === 'DOMAIN_NOT_WHITELISTED' || 
          (error.message && error.message.includes('Domain not whitelisted')))) {
        return; // Already handled by domainError event
      }
      this.textInterface.showError(error.message || error);
      this.textInterface.stopStreamingState();
    });

    this.sdk.on('chunk', (chunk) => {
      this.textInterface.appendStreamingChunk(chunk);
    });

    this.sdk.on('done', ({ text }) => {
      this.textInterface.finalizeStreaming(text);
    });

    // Handle tool widget messages (show_media, dismiss_media) inline in text chat.
    // Images are rendered inline in the agent's response bubble at the point they arrive
    // between text chunks, preserving the natural ordering from the LLM.
    this.sdk.on('message', (message) => {
      const type = message.t || message.type;
      if (type === 'show_media' && message.images && message.images.length > 0) {
        this.textInterface.appendStreamingMedia(message.images, message.title);
      }
      // dismiss_media is ignored for text chat — no overlay to dismiss
    });
  }

  /**
   * Create shadow host and attach shadow DOM
   */
  createShadowHost() {
    // Remove existing shadow host if it exists
    const existingHost = document.getElementById('ttp-widget-shadow-host');
    if (existingHost) {
      existingHost.remove();
    }
    
    // Create shadow host element
    const host = document.createElement('div');
    host.id = 'ttp-widget-shadow-host';
    // IMPORTANT: Host covers viewport but is click-through (pointer-events: none)
    // The actual button and panel have pointer-events: auto to receive clicks
    // Using width/height 100% ensures fixed-position children have proper containing block
    host.style.cssText = 'position: fixed; z-index: 10000; pointer-events: none; top: 0; left: 0; width: 100%; height: 100%; overflow: visible;';
    host.setAttribute('dir', this.getEffectiveTextDirection());
    
    // Attach shadow DOM
    this.shadowRoot = host.attachShadow({ mode: 'open' });
    
    // Append shadow host to body
    document.body.appendChild(host);
  }

  /**
   * Create regular container (no Shadow DOM) for Shopify compatibility
   */
  createRegularContainer() {
    // Remove existing container if it exists
    const existingContainer = document.getElementById('ttp-widget-container');
    if (existingContainer) {
      existingContainer.remove();
    }
    
    // Create regular container element
    const container = document.createElement('div');
    container.id = 'ttp-widget-container';
    // Container covers viewport but is click-through (pointer-events: none)
    // The actual button and panel have pointer-events: auto to receive clicks
    container.style.cssText = 'position: fixed; z-index: 10000; pointer-events: none; top: 0; left: 0; width: 100%; height: 100%; overflow: visible;';
    container.setAttribute('dir', this.getEffectiveTextDirection());
    
    // Append container to body
    document.body.appendChild(container);
    this.regularContainer = container;
  }

  createWidget() {
    // Ensure container exists
    if (!this.container) {
      if (this.config.useShadowDOM) {
        this.createShadowHost();
        this.container = this.shadowRoot;
      } else {
        this.createRegularContainer();
        this.container = this.regularContainer;
        this.shadowRoot = document;
      }
    }
    
    // Match generateCSS(): only append !important in regular DOM (theme override)
    const important = this.config.useShadowDOM === false ? ' !important' : '';
    
    // Generate CSS reset based on mode
    let resetCSS = '';
    if (this.config.useShadowDOM) {
      // Shadow DOM mode - use :host selector
      resetCSS = `
        :host {
          all: initial${important};
          display: block${important};
          pointer-events: none${important};
        }
        img {
          max-width: none !important;
        }
        #text-chat-button img {
          width: 22px !important;
          height: 22px !important;
        }
        #text-chat-button {
          pointer-events: auto${important};
        }
        #text-chat-panel {
          pointer-events: auto${important};
        }
        .ttp-mobile-fab {
          pointer-events: auto${important};
        }
        #ttpMobileLanding {
          pointer-events: auto${important};
        }
      `;
    } else {
      // Regular DOM mode - targeted reset without breaking widget styles
      resetCSS = `
        /* Reset only problematic inherited styles, not all styles */
        #text-chat-button,
        #text-chat-panel,
        #text-chat-panel * {
          box-sizing: border-box${important};
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif${important};
        }
        
        /* Theme often sets img { max-width: 100% } which squashes widget art.
           Do NOT set width/height: auto on all #text-chat-panel img — that uses
           !important and overrides ecommerce card images (.ttp-product-image img
           needs width/height 100% + object-fit), which shows as empty/blank tiles. */
        #text-chat-panel img {
          max-width: none${important};
        }
        
        #text-chat-button img {
          max-width: none${important};
          width: 22px !important;
          height: 22px !important;
        }
        
        /* Ensure button and panel can receive clicks */
        #text-chat-button,
        #text-chat-panel,
        .ttp-mobile-fab,
        #ttpMobileLanding {
          pointer-events: auto${important};
        }
        
        /* Reset any inherited text styles that might break layout */
        #text-chat-panel {
          text-align: left${important};
          line-height: normal${important};
          letter-spacing: normal${important};
          word-spacing: normal${important};
          text-transform: none${important};
          text-indent: 0${important};
          text-shadow: none${important};
          white-space: normal${important};
        }
        
        /* Flex layout for shell only — do NOT force display on .voice-interface /
           .text-interface: #text-chat-panel + class beats component rules and keeps
           both visible (breaks Shopify / useShadowDOM: false). */
        #text-chat-panel .widget-shell,
        #text-chat-panel .panel-inner,
        #text-chat-panel .widget-container,
        #text-chat-panel .widget-header {
          display: flex${important};
        }
        
        #text-chat-panel .widget-shell,
        #text-chat-panel .panel-inner,
        #text-chat-panel .widget-container {
          flex-direction: column${important};
        }
        
        /* Ensure proper stacking */
        #text-chat-button {
          z-index: 10001 !important;
        }
        
        #text-chat-panel {
          z-index: 10000 !important;
        }
      `;
    }
    
    const widgetHTML = this.generateWidgetHTML();
    
    if (this.config.useShadowDOM) {
      // Shadow DOM mode - include reset CSS in HTML and set innerHTML
      const fullHTML = `
        <style>${resetCSS}</style>
        ${widgetHTML}
      `;
      this.shadowRoot.innerHTML = fullHTML;
    } else {
      // Regular DOM mode - inject styles into head and append HTML to container
      this.injectStylesIntoHead(resetCSS);
      this.container.innerHTML = widgetHTML;
    }
    
    this.setupWidgetEvents();
    
    // On desktop, ensure voice hero is shown for unified/voice-only (in-panel mode picker removed)
    if (window.innerWidth > 768) {
      const widgetMode = this.config.behavior?.mode || 'unified';
      const preferVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
      if (preferVoice && this.currentView !== 'text') {
        this.showVoice();
      }
    }
    
    // Ensure initial open state if configured
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel) {
      if (this.config.behavior.startOpen || this.config.behavior.autoOpen) {
        if (window.innerWidth <= 768) {
          // Mobile: show mobile landing overlay
          const mobileFab = this.shadowRoot?.querySelector('.ttp-mobile-fab');
          const mobileLanding = this.shadowRoot?.getElementById('ttpMobileLanding');
          if (mobileFab && mobileLanding) {
            mobileFab.style.display = 'none';
            mobileLanding.classList.add('active');
            this.isMobileLandingOpen = true;
          }
          this.hidePrompt();
        } else {
          panel.style.removeProperty('display');
          this.isOpen = true;
          panel.classList.add('open');
          this.hidePrompt();
        }
      } else {
        // Widget starts closed - show prompt
        this.showPrompt();
      }
    } else {
      // Panel not found yet, but widget should start closed - show prompt
      // Use setTimeout to ensure prompt HTML is generated first
      setTimeout(() => {
        this.showPrompt();
      }, 100);
    }
    
    // Also ensure prompt visibility is correct after a short delay
    // This handles cases where setupPromptAnimation was called before widget HTML was ready
    setTimeout(() => {
      if (!this.isOpen) {
        this.showPrompt();
      } else {
        this.hidePrompt();
      }
    }, 200);
  }

  /**
   * Inject styles into document head (for non-Shadow DOM mode)
   */
  injectStylesIntoHead(resetCSS) {
    // Remove existing style tag if present
    const existingStyle = document.getElementById('ttp-widget-styles');
    if (existingStyle) {
      existingStyle.remove();
    }
    
    // Calculate button and icon sizes (same logic as generateWidgetHTML)
    const buttonSizes = {
      small: 50,
      medium: 60,
      large: 70,
      xl: 80
    };
    const buttonSize = buttonSizes[this.config.button?.size] || 60;
    
    const iconSizes = {
      small: 20,
      medium: 28,
      large: 36,
      xl: 44
    };
    const iconSize = iconSizes[this.config.icon?.size] || 28;
    
    // Generate full CSS
    const positionStyles = this.generatePositionStyles();
    const widgetCSS = this.generateCSS(positionStyles, buttonSize, iconSize);
    
    // Create and inject style tag
    const style = document.createElement('style');
    style.id = 'ttp-widget-styles';
    style.textContent = resetCSS + '\n' + widgetCSS;
    
    // Add custom styles if provided
    if (this.config.customStyles) {
      style.textContent += '\n' + this.config.customStyles;
    }
    
    document.head.appendChild(style);
  }

  generateWidgetHTML() {
    const pos = this.config.position;
    const btn = this.config.button;
    const icon = this.config.icon;
    const panel = this.config.panel;
    const header = this.config.header;
    const anim = this.config.animation;
    

    // Calculate button size
    const buttonSizes = {
      small: 50,
      medium: 60,
      large: 70,
      xl: 80
    };
    const buttonSize = buttonSizes[btn.size] || 60;

    // Calculate icon size
    const iconSizes = {
      small: 20,
      medium: 28,
      large: 36,
      xl: 44
    };
    const iconSize = iconSizes[icon.size] || 28;

    // Generate position styles
    const positionStyles = this.generatePositionStyles();

    const widgetMode = this.config.behavior?.mode || 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    const voiceEnabled = showVoice;
    
    // Helper function to get translated text
    const t = (key) => {
      const lang = this.config.language || 'en';
      const translations = this.translations[lang] || this.translations.en;
      return translations[key] || key;
    };
    
    // Helper function to get tooltip text
    const getTooltip = (key) => {
      const tooltip = this.config.tooltips?.[key];
      if (tooltip !== null && tooltip !== undefined) return tooltip;
      // Use translations for default tooltips
      const defaults = {
        newChat: t('newChat'),
        back: t('back'),
        close: t('close'),
        mute: t('mute'),
        speaker: t('speaker'),
        endCall: t('endCall')
      };
      return defaults[key] || '';
    };

    // Only include style tag in Shadow DOM mode (styles injected into head for regular DOM)
    const styleTag = this.config.useShadowDOM ? `
      <style>
        ${this.generateCSS(positionStyles, buttonSize, iconSize)}
        ${this.config.customStyles}
      </style>
    ` : '';
    
    // Generate prompt bubble HTML if enabled
    const promptConfig = this.config.promptAnimation || {};
    // Default to disabled if not specified (enabled === true means explicitly enabled)
    const isPromptEnabled = promptConfig.enabled === true;
    const promptBubbleHTML = isPromptEnabled ? this.generatePromptBubbleHTML(promptConfig, buttonSize) : '';
    
    console.log('📝 generateWidgetHTML - promptAnimation:', {
      hasConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      willGenerateHTML: !!promptBubbleHTML
    });
    
    const pillTitle = (header.pillTitle != null && String(header.pillTitle).trim() !== '')
      ? String(header.pillTitle).trim()
      : (header.title || 'Assistant');
    /* Mobile FAB: optional header.mobileLabel; otherwise same main line as desktop pill (pillTitle). Not header.mobileLabel default 'Chat' — that hid pillTitle. */
    const mobileFabLabel = (header.mobileLabel != null && String(header.mobileLabel).trim() !== '')
      ? String(header.mobileLabel).trim()
      : pillTitle;
    const mobileLandingHeroName = (this.config.agentName != null && String(this.config.agentName).trim() !== '')
      ? String(this.config.agentName).trim()
      : (header.title || 'Chat Assistant');

    const pillCustomImage = icon.customImage != null && String(icon.customImage).trim() !== ''
      ? String(icon.customImage).trim()
      : null;
    const pillWaveformBars =
      '<span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span>';
    const pillIconInner = pillCustomImage
      ? `<img class="ttp-launcher-logo" src="${pillCustomImage}" alt="" />`
      : `<div class="ttp-pill-waveform" aria-hidden="true">${pillWaveformBars}</div>`;
    const pillIconWrapClass = pillCustomImage ? 'ttp-pill-icon' : 'ttp-pill-icon ttp-pill-icon--wave';

    return `
      ${styleTag}
      
      ${this.config.behavior.hidden ? '' : `
      <div id="text-chat-button-container">
        ${promptBubbleHTML}
        ${isPromptEnabled && promptConfig.showPulseRings !== false ? this.generatePulseRingsHTML(promptConfig) : ''}
        <!-- Desktop pill launcher (hidden on mobile) -->
        <button type="button" id="text-chat-button" aria-label="Open chat with ${pillTitle}">
          <div class="${pillIconWrapClass}">
            ${pillIconInner}
          </div>
          <div class="ttp-pill-text">
            <span class="ttp-pill-title">${pillTitle}</span>
            <span class="ttp-pill-status">
              <span class="ttp-launcher-dot"></span>
              ${header.onlineIndicatorText !== undefined && header.onlineIndicatorText !== null ? header.onlineIndicatorText : t('online')}
            </span>
          </div>
        </button>
        <!-- Mobile FAB (hidden on desktop) -->
        <button class="ttp-mobile-fab" aria-label="${this.config.accessibility.ariaLabel}">
          <div class="ttp-mobile-fab__icon-wrap">
            <div class="ttp-mobile-fab__waveform">
              <span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span>
            </div>
          </div>
          <div class="ttp-mobile-fab__label-wrap">
            <span class="ttp-mobile-fab__label">${mobileFabLabel}</span>
            <span class="ttp-mobile-fab__status">
              <span class="ttp-mobile-fab__dot"></span>
              ${header.onlineIndicatorText !== undefined && header.onlineIndicatorText !== null ? header.onlineIndicatorText : t('online')}
            </span>
          </div>
        </button>
      </div>
      `}

      ${this.config.behavior.hidden ? '' : `
      <div id="ttpMobileLanding" class="ttp-mobile-landing">
        <div class="ttp-mobile-landing__header">
          <div class="ttp-mobile-landing__avatar">
            <div class="ttp-mobile-landing__waveform">
              <span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span>
            </div>
          </div>
          <div class="ttp-mobile-landing__info">
            <div class="ttp-mobile-landing__name">${mobileLandingHeroName}</div>
            <div class="ttp-mobile-landing__status">
              <span class="ttp-mobile-landing__dot"></span>
              ${this.config.landing.statusText || t('online') + ' • ' + (this.config.landing.subtitle || 'Ready to help')}
            </div>
          </div>
          <button class="ttp-mobile-landing__close" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="ttp-mobile-landing__error" id="ttpMobileLandingError" style="display:none;">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span class="ttp-mobile-landing__error-text"></span>
        </div>
        <div class="ttp-mobile-landing__actions">
          ${showVoice ? `
          <button class="ttp-mobile-landing__btn ttp-mobile-landing__btn--call" id="ttpMobileCallBtn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            ${this.config.landing.callButtonText || this.config.landing.voiceCardTitle || t('voiceCall')}
          </button>
          ` : ''}
          ${showText ? `
          <button class="ttp-mobile-landing__btn ttp-mobile-landing__btn--text" id="ttpMobileChatBtn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            ${this.config.landing.chatButtonText || this.config.landing.textCardTitle || t('textChat')}
          </button>
          ` : ''}
        </div>
        ${this.config.footer.show !== false ? `
        <div class="ttp-mobile-landing__footer">
          <div class="ttp-mobile-landing__powered">
            <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            Powered by <a href="${this.config.footer.brand === 'speacart' ? 'https://speacart.com' : 'https://talktopc.com'}" target="_blank" rel="noopener">${this.config.footer.brand === 'speacart' ? 'SpeaCart' : 'TalkToPC'}</a>
          </div>
        </div>
        ` : ''}
      </div>
      `}
      
      <div id="text-chat-panel">
        <div class="widget-shell">
          <div class="panel-inner widget-container" style="direction: ${this.config.direction};">

          ${showVoice ? this.voiceInterface.generateHTML() : ''}
          ${showText ? this.textInterface.generateHTML() : ''}
          ${this.generateFooterHTML()}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Resolved footer branding for panel + desktop voice strip.
   * @returns {{ brandUrl: string, brandName: string } | null}
   */
  getFooterBranding() {
    const footer = this.config.footer;
    if (!footer?.show) return null;

    const refSource = encodeURIComponent(window.location.hostname || 'widget');
    const isSpeacart = footer.brand === 'speacart';
    return {
      brandUrl: isSpeacart
        ? 'https://speacart.com'
        : `https://talktopc.com?ref=widget&source=${refSource}`,
      brandName: isSpeacart ? 'SpeaCart' : 'TalkToPC',
    };
  }

  /**
   * Generate footer branding HTML
   */
  generateFooterHTML() {
    const branding = this.getFooterBranding();
    if (!branding) return '';

    const { brandUrl, brandName } = branding;
    return `
      <div class="widget-footer ttp-footer">
        <span>Powered by <a href="${brandUrl}" target="_blank" rel="noopener noreferrer"><b>${brandName}</b></a></span>
      </div>
    `;
  }

  /**
   * Hide in-panel footer while the desktop bottom strip owns the active call UI.
   * @param {boolean} visible
   */
  _setPanelFooterVisible(visible) {
    const root = this.shadowRoot || document;
    const footer = root.querySelector?.('.ttp-footer');
    if (footer) footer.style.display = visible ? '' : 'none';
  }

  generatePositionStyles() {
    const pos = this.config.position;
    const offset = pos.offset || { x: 20, y: 20 };
    
    let styles = '';
    
    // Vertical positioning
    if (pos.vertical === 'top') {
      styles += `top: ${offset.y}px;`;
    } else if (pos.vertical === 'bottom') {
      styles += `bottom: ${offset.y}px;`;
    } else if (pos.vertical === 'center') {
      styles += `top: 50%; transform: translateY(-50%);`;
    }
    
    // Horizontal positioning
    if (pos.horizontal === 'left') {
      styles += `left: ${offset.x}px;`;
    } else if (pos.horizontal === 'right') {
      styles += `right: ${offset.x}px;`;
    } else if (pos.horizontal === 'center') {
      styles += `left: 50%; transform: translateX(-50%);`;
    }
    
    return styles;
  }

  generateIconHTML(size) {
    const icon = this.config.icon;
    
    switch (icon.type) {
      case 'custom':
        if (icon.customImage != null && String(icon.customImage).trim() !== '') {
          return `<img src="${icon.customImage}" alt="Chat Assistant" style="width: ${size}px; height: ${size}px; object-fit: contain;" />`;
        }
        return `<div class="ttp-pill-waveform" aria-hidden="true"><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span><span class="bar"></span></div>`;
      
      case 'emoji':
        return `<span style="font-size: ${size}px; line-height: 1;">${icon.emoji}</span>`;
      
      case 'text':
        return `<span style="font-size: ${Math.floor(size * 0.6)}px; font-weight: bold; color: white;">${icon.text}</span>`;
      
      case 'microphone':
      default:
        return `<svg viewBox="0 0 24 24" style="width: ${size}px; height: ${size}px; fill: white;">
          <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
          <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
        </svg>`;
      
      case 'chat':
        return `<svg viewBox="0 0 24 24" style="width: ${size}px; height: ${size}px; fill: white;">
          <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
        </svg>`;
    }
  }

  /**
   * Generate prompt bubble HTML
   */
  generatePromptBubbleHTML(promptConfig, buttonSize) {
    // Extract solid color from gradient for arrow (fallback)
    let arrowColor = '#7c3aed';
    if (promptConfig.backgroundColor && promptConfig.backgroundColor.includes('gradient')) {
      // Try to extract first color from gradient
      const match = promptConfig.backgroundColor.match(/#[0-9a-fA-F]{6}/);
      if (match) {
        arrowColor = match[0];
      }
    } else {
      arrowColor = promptConfig.backgroundColor;
    }
    
    const animationClass = promptConfig.animationType === 'none' ? '' : `animation-${promptConfig.animationType}`;
    const shimmerHTML = promptConfig.showShimmer ? '<div class="prompt-bubble-shimmer"></div>' : '';
    
    return `
      <div class="prompt-bubble ${promptConfig.position} ${animationClass}" 
           id="prompt-bubble"
           role="tooltip"
           style="--prompt-bubble-bg-color: ${arrowColor};">
        <div class="prompt-bubble-content" 
             style="background: ${promptConfig.backgroundColor}; color: ${promptConfig.textColor};">
          ${shimmerHTML}
          <span style="position: relative; z-index: 1;">${promptConfig.text}</span>
        </div>
        <div class="prompt-bubble-arrow"></div>
      </div>
    `;
  }

  /**
   * Generate pulse rings HTML
   */
  generatePulseRingsHTML(promptConfig) {
    // Extract solid color from gradient for rings (fallback)
    let ringColor = '#7c3aed';
    if (promptConfig.backgroundColor && promptConfig.backgroundColor.includes('gradient')) {
      const match = promptConfig.backgroundColor.match(/#[0-9a-fA-F]{6}/);
      if (match) {
        ringColor = match[0];
      }
    } else {
      ringColor = promptConfig.backgroundColor;
    }
    
    return `
      <div class="prompt-pulse-rings" id="prompt-pulse-rings">
        <div class="prompt-pulse-ring" style="background-color: ${ringColor}33;"></div>
        <div class="prompt-pulse-ring" style="background-color: ${ringColor}1a;"></div>
      </div>
    `;
  }

  generateCSS(positionStyles, buttonSize, iconSize) {
    const btn = this.config.button;
    const icon = this.config.icon;
    const panel = this.config.panel;
    const header = this.config.header;
    const messages = this.config.messages;
    const anim = this.config.animation;
    
    // Determine which interfaces to show
    const widgetMode = this.config.behavior?.mode || 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    
    // Add !important to display rules when not using Shadow DOM (to override theme CSS)
    const important = this.config.useShadowDOM === false ? ' !important' : '';
    
    // Pill launcher colors from voice config
    const voice = this.config.voice || {};
    const pillGradient = voice.pillGradient || 'linear-gradient(135deg, #581c87, #312e81, #1e1b4b)';
    const pillTextColor = voice.pillTextColor || '#ffffff';
    const pillDotColor = voice.pillDotColor || '#4ade80';

    // Detect light vs dark panel background for adaptive footer
    const panelBg = panel.backgroundColor || '#16161e';
    const isLightPanel = computeIsLightPanel(panelBg);
    const footerTextColor = isLightPanel ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.35)';
    const footerLinkColor = talkToPcLinkColorForPanel(isLightPanel, voice, this._rawConfig, showVoice);
    const footerBorderColor = isLightPanel ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)';
    const idleHeroGrad1 = voice.heroGradient1 || '#2a2550';
    const idleHeroGrad2 = voice.heroGradient2 || '#1a1a2e';
    const callSurfaceGradient = `linear-gradient(160deg, ${idleHeroGrad1} 0%, ${idleHeroGrad2} 100%)`;
    const textUsesVoiceTheme = this.config.text?.useVoiceTheme !== false;
    const footerLinkOnDarkHero = talkToPcLinkColorForPanel(false, voice, this._rawConfig, showVoice);
    /** In-call panel/footer: same gradient as idle so active call matches landing (hero1 → hero2) */
    const callActivePanelBg = callSurfaceGradient;
    const voiceIdleVisibleSelector =
      '#voiceInterface.active #voiceIdleState:not([style*="display: none"]):not([style*="display:none"])';
    const voiceActiveVisibleSelector =
      '#voiceInterface.active #voiceActiveState:not([style*="display: none"]):not([style*="display:none"])';

    return `
      /* Inline style display overrides - ensures JS show/hide works with !important CSS */
      #text-chat-panel [style*="display: none"],
      #text-chat-panel [style*="display:none"],
      #text-chat-button-container [style*="display: none"],
      #text-chat-button-container [style*="display:none"],
      #ttpMobileLanding[style*="display: none"],
      #ttpMobileLanding[style*="display:none"] {
        display: none !important;
      }
      #text-chat-panel [style*="display: flex"],
      #text-chat-panel [style*="display:flex"],
      #text-chat-button-container [style*="display: flex"],
      #text-chat-button-container [style*="display:flex"] {
        display: flex !important;
      }
      #text-chat-panel [style*="display: block"],
      #text-chat-panel [style*="display:block"] {
        display: block !important;
      }

      /* MOBILE FIRST - Default styles for all devices */
      #text-chat-widget {
        position: fixed !important;
        z-index: 10000${important};
        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif${important};
        /* Mobile defaults */
        right: 10px${important};
        bottom: 10px${important};
        left: auto${important};
        top: auto${important};
      }
      
      /* Desktop positioning (only on larger screens) */
      @media (min-width: 769px) {
        #text-chat-widget {
          ${positionStyles}
          right: ${this.config.position.horizontal === 'right' ? '20px' : 'auto'}${important};
          left: ${this.config.position.horizontal === 'left' ? '20px' : 'auto'}${important};
          bottom: ${this.config.position.vertical === 'bottom' ? '20px' : 'auto'}${important};
          top: ${this.config.position.vertical === 'top' ? '20px' : 'auto'}${important};
        }
      }
      
      /* Mobile override (force mobile positioning) */
      @media (max-width: 768px) {
        #text-chat-widget {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
          transform: none !important;
        }
      }
      
      @media (max-width: 480px) {
        #text-chat-widget {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      /* ── Desktop Pill Launcher ── */
      #text-chat-button {
        position: relative${important};
        display: flex${important};
        align-items: center${important};
        gap: 8px${important};
        padding-block: 10px${important};
        padding-inline-start: 6px${important};
        padding-inline-end: 12px${important};
        z-index: 1${important};
        background: ${pillGradient};
        border: 1px solid rgba(139,92,246,0.15)${important};
        border-radius: 999px${important};
        box-shadow: 0 8px 32px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15)${important};
        cursor: pointer${important};
        transition: box-shadow 0.25s, transform 0.2s, padding 0.3s, gap 0.3s${important};
        font-family: inherit${important};
        color: ${pillTextColor};
        overflow: hidden${important};
        box-sizing: border-box${important};
        flex-shrink: 0${important};
      }
      #text-chat-button:hover {
        transform: translateY(-1px)${important};
        box-shadow: 0 10px 40px rgba(0,0,0,0.35), 0 4px 12px rgba(0,0,0,0.2)${important};
      }
      #text-chat-button.open {
        gap: 0px${important};
        padding: 9px${important};
        width: auto${important};
        min-width: 0${important};
        max-width: none${important};
      }

      @media (min-width: 769px) {
        #text-chat-button {
          width: 158px${important};
          min-width: 158px${important};
          max-width: 158px${important};
        }
      }

      .ttp-pill-icon {
        width: 36px${important}; height: 36px${important}; border-radius: 50%${important};
        background: #ffffff${important};
        display: flex${important}; align-items: center${important}; justify-content: center${important};
        flex-shrink: 0${important};
      }
      .ttp-launcher-logo {
        width: 22px${important}; height: 22px${important}; border-radius: 50%${important};
        object-fit: cover${important};
      }

      /* Default pill launcher: same animated waveform as mobile FAB / landing */
      .ttp-pill-icon--wave {
        background: rgba(255, 255, 255, 0.15)${important};
      }
      .ttp-pill-waveform {
        display: flex${important};
        align-items: center${important};
        gap: 2px${important};
        height: 16px${important};
      }
      .ttp-pill-waveform .bar {
        width: 2px${important};
        background: #fff${important};
        border-radius: 1px${important};
        animation: ttpWaveAnimation 0.8s ease-in-out infinite${important};
      }
      .ttp-pill-waveform .bar:nth-child(1) { height: 5px${important}; animation-delay: 0s${important}; }
      .ttp-pill-waveform .bar:nth-child(2) { height: 10px${important}; animation-delay: 0.1s${important}; }
      .ttp-pill-waveform .bar:nth-child(3) { height: 16px${important}; animation-delay: 0.2s${important}; }
      .ttp-pill-waveform .bar:nth-child(4) { height: 8px${important}; animation-delay: 0.3s${important}; }
      .ttp-pill-waveform .bar:nth-child(5) { height: 12px${important}; animation-delay: 0.4s${important}; }

      .ttp-pill-text {
        display: flex${important}; flex-direction: column${important}; gap: 2px${important};
        max-width: 82px${important};
        opacity: 1${important};
        overflow: hidden${important};
        white-space: nowrap${important};
        transition: max-width 0.3s, opacity 0.2s${important};
      }
      #text-chat-button.open .ttp-pill-text {
        max-width: 0${important};
        opacity: 0${important};
      }
      .ttp-pill-title {
        font-size: 13px${important}; font-weight: 600${important}; color: ${pillTextColor};
        line-height: 1.25${important};
        overflow: hidden${important};
        text-overflow: ellipsis${important};
      }
      .ttp-pill-status {
        display: flex${important}; align-items: center${important}; gap: 4px${important};
        font-size: 11px${important}; color: ${pillTextColor}8c;
        line-height: 1.25${important};
      }

      /* Online dot (inside pill status) */
      .ttp-launcher-dot {
        width: 7px${important}; height: 7px${important}; border-radius: 50%${important};
        background: ${pillDotColor};
        flex-shrink: 0${important};
        animation: ttp-dot-pulse 2s ease-in-out infinite${important};
      }
      @keyframes ttp-dot-pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
      
      @media (max-width: 768px) {
        #text-chat-widget {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
          transform: none !important;
        }
        
        #text-chat-button-container {
          width: auto !important;
          height: auto !important;
        }
        
        #text-chat-panel {
          position: fixed !important;
          right: 10px !important;
          left: auto !important;
          bottom: 92px !important; /* 56px button + 20px gap + 16px footer */
          top: auto !important;
          width: 340px !important;
          max-width: calc(100vw - 20px) !important;
          height: auto !important;
          max-height: calc(100vh - 130px) !important;
          transform: none !important;
          margin: 0 !important;
        }
        
        #text-chat-panel .widget-header {
          padding: 10px 14px${important};
          min-height: 56px${important};
        }
        
        #text-chat-panel .header-title {
          font-size: 15px${important};
        }
        
        #text-chat-panel .header-icon {
          width: 40px${important};
          height: 40px${important};
          min-width: 40px${important};
          min-height: 40px${important};
        }
        
        #text-chat-input {
          font-size: 16px !important; /* Prevents iOS zoom on focus */
          padding: 12px 16px !important;
          min-height: 48px !important;
        }
        
        #text-chat-send {
          min-width: 48px !important;
          min-height: 48px !important;
          width: 48px !important;
          height: 48px !important;
        }
        
      }
      
      @media (max-width: 480px) {
        #text-chat-widget {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
        
        #text-chat-button-container {
          width: auto !important;
          height: auto !important;
        }
        
        #text-chat-panel {
          right: 8px !important;
          left: auto !important;
          bottom: 86px !important; /* 54px button + 20px gap + 12px footer */
          top: auto !important;
          width: 340px !important;
          max-width: calc(100vw - 16px) !important;
          height: auto !important;
          max-height: calc(100vh - 120px) !important;
        }
        
        #text-chat-panel .widget-header {
          padding: 8px 12px${important};
          min-height: 52px${important};
        }
        
        #text-chat-panel .header-title {
          font-size: 14px${important};
        }
        
      }
      
      ${anim.enableHover ? `
        #text-chat-button:hover {
          transform: translateY(-1px);
        }
      ` : ''}
      
      /* Prompt Animation Keyframes */
      @keyframes widget-shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
      
      @keyframes widget-ripple {
        0% { transform: scale(1); opacity: 0.6; }
        100% { transform: scale(2.5); opacity: 0; }
      }
      
      @keyframes widget-float {
        0%, 100% { transform: translateX(-50%) translateY(0); }
        50% { transform: translateX(-50%) translateY(-8px); }
      }
      
      @keyframes widget-bounce {
        0%, 100% { transform: translateX(-50%) translateY(0); }
        50% { transform: translateX(-50%) translateY(-10px); }
      }
      
      @keyframes widget-pulse-ring {
        0% { transform: scale(1); opacity: 0.4; }
        100% { transform: scale(1.8); opacity: 0; }
      }
      
      /* Prompt Bubble Container */
      #text-chat-button-container {
        position: fixed${important};
        ${this.config.position.vertical === 'bottom' ? `bottom: ${this.config.position.offset?.y || 20}px;` : `top: ${this.config.position.offset?.y || 20}px;`}
        ${this.config.position.horizontal === 'right' ? `right: ${this.config.position.offset?.x || 20}px;` : `left: ${this.config.position.offset?.x || 20}px;`}
        width: auto${important};
        height: auto${important};
        z-index: 10001${important};
        display: flex${important};
        align-items: center${important};
        justify-content: center${important};
      }
      
      @media (max-width: 768px) {
        #text-chat-button-container {
          right: 10px !important;
          bottom: 10px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      @media (max-width: 480px) {
        #text-chat-button-container {
          right: 8px !important;
          bottom: 8px !important;
          left: auto !important;
          top: auto !important;
        }
      }
      
      /* Prompt Bubble Styles */
      .prompt-bubble {
        position: absolute${important};
        z-index: 10002${important};
        pointer-events: none${important};
        white-space: nowrap${important};
      }
      
      .prompt-bubble.top {
        bottom: calc(100% + 18px)${important};
        left: 50%${important};
        transform: translateX(-50%)${important};
      }
      
      .prompt-bubble.left {
        right: calc(100% + 12px)${important};
        top: 50%${important};
        transform: translateY(-50%)${important};
      }
      
      .prompt-bubble.right {
        left: calc(100% + 12px)${important};
        top: 50%${important};
        transform: translateY(-50%)${important};
      }
      
      /* Ensure animations preserve horizontal centering for top position */
      .prompt-bubble.top.animation-bounce {
        animation: widget-bounce 1s ease-in-out infinite${important};
        transform: translateX(-50%)${important}; /* Keep centering */
      }
      
      .prompt-bubble.top.animation-float {
        animation: widget-float 2s ease-in-out infinite${important};
        transform: translateX(-50%)${important}; /* Keep centering */
      }
      
      .prompt-bubble.top.animation-pulse {
        animation: pulse 2s ease-in-out infinite${important};
        transform: translateX(-50%)${important}; /* Keep centering */
      }
      
      .prompt-bubble-content {
        position: relative${important};
        padding: 8px 16px${important};
        border-radius: 20px${important};
        font-weight: 500${important};
        font-size: 14px${important};
        box-shadow: 0 4px 15px rgba(124, 58, 237, 0.3)${important};
        overflow: hidden${important};
      }
      
      .prompt-bubble-shimmer {
        position: absolute${important};
        inset: 0${important};
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.25), transparent)${important};
        animation: widget-shimmer 2s infinite${important};
      }
      
      .prompt-bubble.animation-bounce {
        animation: widget-bounce 1s ease-in-out infinite${important};
      }
      
      .prompt-bubble.animation-pulse {
        animation: pulse 2s ease-in-out infinite${important};
      }
      
      .prompt-bubble.animation-float {
        animation: widget-float 2s ease-in-out infinite${important};
      }
      
      /* Ensure top-positioned bubbles maintain horizontal centering during animations */
      .prompt-bubble.top.animation-bounce,
      .prompt-bubble.top.animation-float,
      .prompt-bubble.top.animation-pulse {
        /* Animation keyframes already include translateX(-50%) for centering */
      }
      
      /* Prompt Bubble Arrow */
      .prompt-bubble-arrow {
        position: absolute${important};
        width: 0${important};
        height: 0${important};
        border: 8px solid transparent${important};
      }
      
      .prompt-bubble.top .prompt-bubble-arrow {
        top: 100%${important};
        left: 50%${important};
        transform: translateX(-50%)${important};
        border-top-color: var(--prompt-bubble-bg-color, #7c3aed)${important};
        border-bottom: none${important};
        margin-left: 0${important};
      }
      
      .prompt-bubble.left .prompt-bubble-arrow {
        left: 100%${important};
        top: 50%${important};
        transform: translateY(-50%)${important};
        border-left-color: var(--prompt-bubble-bg-color, #7c3aed)${important};
        border-right: none${important};
      }
      
      .prompt-bubble.right .prompt-bubble-arrow {
        right: 100%${important};
        top: 50%${important};
        transform: translateY(-50%)${important};
        border-right-color: var(--prompt-bubble-bg-color, #7c3aed)${important};
        border-left: none${important};
      }
      
      /* Pulse Rings */
      .prompt-pulse-rings {
        position: absolute${important};
        inset: 0${important};
        border-radius: 50%${important};
        pointer-events: none${important};
      }
      
      .prompt-pulse-ring {
        position: absolute${important};
        inset: 0${important};
        border-radius: 50%${important};
        animation: widget-pulse-ring 2s ease-out infinite${important};
      }
      
      .prompt-pulse-ring:nth-child(2) {
        animation-delay: 0.5s${important};
      }
      
      /* Mobile adjustments for prompt bubble */
      @media (max-width: 768px) {
        .prompt-bubble-content {
          font-size: 12px${important};
          padding: 6px 12px${important};
        }
        
        .prompt-bubble.top {
          bottom: calc(100% + 14px)${important};
        }
        
        .prompt-bubble.left,
        .prompt-bubble.right {
          display: none${important}; /* Hide side prompts on mobile */
        }
      }
      
      /* Shared panel chrome; size/position differ — desktop card vs mobile sheet (see @media blocks earlier) */
      #text-chat-panel {
        display: none${important};
        position: fixed${important};
        background: ${panelBg};
        border-radius: 20px${important};
        border: ${panel.border || '1px solid rgba(255,255,255,0.08)'};
        flex-direction: column${important};
        overflow: hidden${important};
        box-shadow: 0 24px 60px rgba(0,0,0,0.6)${important};
        opacity: 0${important};
        pointer-events: none${important};
        box-sizing: border-box${important};
      }

      /* Desktop / tablet: floating 340px card anchored to launcher */
      @media (min-width: 769px) {
        #text-chat-panel {
          bottom: calc(54px + 24px)${important}; /* launcher pill + gap */
          ${this.config.position.horizontal === 'right' ? 'right: 20px;' : 'left: 20px;'}
          width: 340px${important};
          max-width: calc(100vw - 40px)${important};
          height: auto${important};
          max-height: calc(100vh - 100px)${important};
          transform-origin: bottom right${important};
        }
        /* Text chat: fixed height so the message list scrolls instead of growing the panel per message */
        #text-chat-panel:has(.text-interface.active) {
          height: min(520px, calc(100vh - 100px))${important};
        }
      }
      
      #text-chat-panel.open {
        display: flex${important};
        opacity: 1${important};
        pointer-events: auto${important};
      }

      /* Shell for panel inner */
      .widget-shell { width: 100%${important}; padding: 0${important}; border-radius: 20px${important}; background: transparent${important}; overflow: hidden${important}; display: flex${important}; flex-direction: column${important}; box-sizing: border-box${important}; flex: 1${important}; min-height: 0${important}; }
      .panel-inner { width: 100%${important}; background: ${panelBg}; border-radius: 20px${important}; border: none${important}; overflow: hidden${important}; display:flex${important}; flex-direction: column${important}; padding: 0${important}; box-sizing: border-box${important}; max-width: 100%${important}; flex: 1${important}; min-height: 0${important}; }

      #text-chat-panel .widget-container {
        width: 100%${important}; background: ${panelBg}; overflow: visible${important}; display: flex${important}; flex-direction: column${important}; border-radius: 20px${important}; flex: 1${important}; min-height: 0${important};
      }

      /* Voice idle: one gradient for voice UI + footer (Powered by), not a flat strip under buttons */
      #text-chat-panel.open:has(${voiceIdleVisibleSelector}) {
        background: linear-gradient(160deg, ${idleHeroGrad1} 0%, ${idleHeroGrad2} 100%)${important};
      }
      #text-chat-panel .widget-container:has(${voiceIdleVisibleSelector}) {
        background: linear-gradient(160deg, ${idleHeroGrad1} 0%, ${idleHeroGrad2} 100%)${important};
      }

      /* Voice active call: same hero gradient as idle + mobile minimized bar */
      #text-chat-panel.open:has(${voiceActiveVisibleSelector}) {
        background: ${callActivePanelBg}${important};
      }
      #text-chat-panel .widget-container:has(${voiceActiveVisibleSelector}) {
        background: ${callActivePanelBg}${important};
      }
      #text-chat-panel.open:has(${voiceActiveVisibleSelector}) .widget-shell {
        background: ${callActivePanelBg}${important};
      }
      #text-chat-panel.open:has(${voiceActiveVisibleSelector}) .panel-inner {
        background: ${callActivePanelBg}${important};
      }
      #text-chat-panel.open:has(${voiceActiveVisibleSelector}) .ttp-footer {
        background: ${callActivePanelBg}${important};
      }

      ${textUsesVoiceTheme ? `
      /* Text chat with voice theme: same hero surface as voice idle; inner shells transparent */
      #text-chat-panel.open:has(.text-interface.active) {
        background: ${callSurfaceGradient}${important};
      }
      #text-chat-panel.open:has(.text-interface.active) .widget-shell,
      #text-chat-panel.open:has(.text-interface.active) .panel-inner,
      #text-chat-panel.open:has(.text-interface.active) .widget-container {
        background: transparent${important};
      }
      #text-chat-panel.open:has(.text-interface.active) .ttp-footer {
        background: transparent${important};
        border-top-color: rgba(255,255,255,0.06)${important};
      }
      #text-chat-panel.open:has(.text-interface.active) .ttp-footer span {
        color: rgba(255,255,255,0.35)${important};
      }
      #text-chat-panel.open:has(.text-interface.active) .ttp-footer a {
        color: ${footerLinkOnDarkHero}${important};
      }
      ` : ''}
      
      /* Voice may scroll as a whole; text chat keeps a fixed shell and scrolls #messagesContainer only */
      #text-chat-panel .widget-container > .voice-interface {
        flex: 1${important};
        min-height: 0${important};
        overflow-y: auto${important};
        overflow-x: visible${important};
      }
      #text-chat-panel .widget-container > .text-interface {
        flex: 1${important};
        min-height: 0${important};
        overflow: hidden${important};
        overflow-x: visible${important};
      }
      
      /* Header is removed in new design - hero serves as header */
      #text-chat-panel .widget-header { display: none${important}; }

      ${showVoice ? this.voiceInterface.generateCSS() : ''}
      ${showText ? this.textInterface.generateCSS() : ''}
      
      /* Footer Branding */
      .ttp-footer {
        padding: 6px 18px 10px${important};
        border-top: 1px solid ${footerBorderColor};
        display: flex${important};
        justify-content: center${important};
        flex-shrink: 0${important};
        box-sizing: border-box${important};
        background: transparent${important};
      }
      .ttp-footer span { font-size: 10px${important}; color: ${footerTextColor}; }
      .ttp-footer a { color: ${footerLinkColor}; text-decoration: none${important}; font-weight: 600${important}; }
      .ttp-footer a:hover { opacity: 0.85${important}; }
      .ttp-footer a b { font-weight: 700${important}; }
      
      #text-chat-send-hint {
        text-align: center${important};
        line-height: 1.4${important};
      }
      
      .agent-thinking {
        font-style: italic${important};
        color: #6B7280${important};
      }

      /* ========================================
         MOBILE PILL BUTTON & LANDING OVERLAY
         Hidden on desktop, shown on mobile only
         ======================================== */
      .ttp-mobile-fab {
        display: none${important};
      }

      .ttp-mobile-landing {
        display: none${important};
      }

      @keyframes ttpWaveAnimation {
        0%, 100% { transform: scaleY(0.4); }
        50% { transform: scaleY(1); }
      }

      @keyframes ttpPulseAnimation {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.5; }
      }

      @media (max-width: 768px) {
        /* Hide desktop pill button on mobile */
        #text-chat-button {
          display: none !important;
        }

        /* Re-flow the container for pill shape */
        #text-chat-button-container {
          width: auto !important;
          height: auto !important;
          min-width: 0 !important;
          min-height: 0 !important;
          max-width: none !important;
          max-height: none !important;
        }

        /* Show mobile pill button */
        .ttp-mobile-fab {
          display: flex${important};
          align-items: center${important};
          gap: 10px${important};
          padding: 10px 16px 10px 12px${important};
          background: ${pillGradient};
          border: none${important};
          border-radius: 24px${important};
          cursor: pointer${important};
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3)${important};
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1)${important};
          z-index: 1${important};
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif${important};
          touch-action: manipulation${important};
          -webkit-tap-highlight-color: transparent${important};
        }

        .ttp-mobile-fab:hover {
          transform: translateY(-3px)${important};
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4)${important};
        }

        .ttp-mobile-fab__icon-wrap {
          width: 36px${important};
          height: 36px${important};
          border-radius: 50%${important};
          background: rgba(255, 255, 255, 0.15)${important};
          display: flex${important};
          align-items: center${important};
          justify-content: center${important};
          flex-shrink: 0${important};
        }

        .ttp-mobile-fab__waveform {
          display: flex${important};
          align-items: center${important};
          gap: 2px${important};
          height: 16px${important};
        }

        .ttp-mobile-fab__waveform .bar {
          width: 2px${important};
          background: #fff${important};
          border-radius: 1px${important};
          animation: ttpWaveAnimation 0.8s ease-in-out infinite${important};
        }

        .ttp-mobile-fab__waveform .bar:nth-child(1) { height: 5px${important}; animation-delay: 0s${important}; }
        .ttp-mobile-fab__waveform .bar:nth-child(2) { height: 10px${important}; animation-delay: 0.1s${important}; }
        .ttp-mobile-fab__waveform .bar:nth-child(3) { height: 16px${important}; animation-delay: 0.2s${important}; }
        .ttp-mobile-fab__waveform .bar:nth-child(4) { height: 8px${important}; animation-delay: 0.3s${important}; }
        .ttp-mobile-fab__waveform .bar:nth-child(5) { height: 12px${important}; animation-delay: 0.4s${important}; }

        .ttp-mobile-fab__label-wrap {
          display: flex${important};
          flex-direction: column${important};
          align-items: flex-start${important};
          gap: 1px${important};
        }

        .ttp-mobile-fab__label {
          color: ${pillTextColor};
          font-size: 14px${important};
          font-weight: 600${important};
          line-height: 1.2${important};
        }

        .ttp-mobile-fab__status {
          display: flex${important};
          align-items: center${important};
          gap: 4px${important};
          font-size: 11px${important};
          color: ${pillTextColor}b3;
          line-height: 1.2${important};
        }

        .ttp-mobile-fab__dot {
          width: 6px${important};
          height: 6px${important};
          background: ${pillDotColor};
          border-radius: 50%${important};
          box-shadow: 0 0 6px ${pillDotColor}99;
        }

        /* ---- Mobile Landing Overlay ---- */
        .ttp-mobile-landing.active {
          display: block${important};
          position: fixed${important};
          bottom: 16px${important};
          left: 12px${important};
          right: 12px${important};
          background: ${pillGradient};
          border-radius: 20px${important};
          overflow: hidden${important};
          box-shadow:
            0 10px 40px rgba(0, 0, 0, 0.3),
            0 0 0 1px rgba(255, 255, 255, 0.1) inset${important};
          z-index: 10002${important};
          font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif${important};
        }

        .ttp-mobile-landing::before {
          content: ''${important};
          position: absolute${important};
          top: 0${important};
          left: 0${important};
          right: 0${important};
          height: 1px${important};
          background: linear-gradient(90deg,
            transparent 0%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 100%);
        }

        .ttp-mobile-landing__header {
          padding: 14px 16px${important};
          display: flex${important};
          align-items: center${important};
          gap: 12px${important};
        }

        .ttp-mobile-landing__avatar {
          width: 44px${important};
          height: 44px${important};
          border-radius: 12px${important};
          background: rgba(255, 255, 255, 0.15)${important};
          display: flex${important};
          align-items: center${important};
          justify-content: center${important};
          flex-shrink: 0${important};
        }

        .ttp-mobile-landing__waveform {
          display: flex${important};
          align-items: center${important};
          gap: 3px${important};
          height: 20px${important};
        }

        .ttp-mobile-landing__waveform .bar {
          width: 3px${important};
          background: rgba(255, 255, 255, 0.9)${important};
          border-radius: 2px${important};
          animation: ttpWaveAnimation 0.8s ease-in-out infinite${important};
        }

        .ttp-mobile-landing__waveform .bar:nth-child(1) { height: 6px${important}; animation-delay: 0s${important}; }
        .ttp-mobile-landing__waveform .bar:nth-child(2) { height: 12px${important}; animation-delay: 0.1s${important}; }
        .ttp-mobile-landing__waveform .bar:nth-child(3) { height: 20px${important}; animation-delay: 0.2s${important}; }
        .ttp-mobile-landing__waveform .bar:nth-child(4) { height: 10px${important}; animation-delay: 0.3s${important}; }
        .ttp-mobile-landing__waveform .bar:nth-child(5) { height: 16px${important}; animation-delay: 0.4s${important}; }

        .ttp-mobile-landing__info {
          flex: 1${important};
          min-width: 0${important};
        }

        .ttp-mobile-landing__name {
          color: #fff${important};
          font-size: 15px${important};
          font-weight: 600${important};
          margin-bottom: 2px${important};
        }

        .ttp-mobile-landing__status {
          color: rgba(255, 255, 255, 0.7)${important};
          font-size: 12px${important};
          display: flex${important};
          align-items: center${important};
          gap: 5px${important};
        }

        .ttp-mobile-landing__dot {
          width: 6px${important};
          height: 6px${important};
          background: ${header.onlineIndicatorDotColor || '#10b981'};
          border-radius: 50%${important};
          animation: ttpPulseAnimation 2s ease-in-out infinite${important};
        }

        .ttp-mobile-landing__close {
          width: 32px${important};
          height: 32px${important};
          border-radius: 10px${important};
          background: rgba(255, 255, 255, 0.1)${important};
          border: none${important};
          color: rgba(255, 255, 255, 0.7)${important};
          display: flex${important};
          align-items: center${important};
          justify-content: center${important};
          cursor: pointer${important};
          transition: all 0.2s${important};
          flex-shrink: 0${important};
        }

        .ttp-mobile-landing__close:hover {
          background: rgba(255, 255, 255, 0.2)${important};
          color: #fff${important};
        }

        .ttp-mobile-landing__error {
          margin: 0 14px 10px${important};
          padding: 10px 12px${important};
          background: #fef2f2${important};
          border: 1px solid #fecaca${important};
          border-radius: 10px${important};
          display: flex${important};
          align-items: flex-start${important};
          gap: 8px${important};
          color: #b91c1c${important};
          font-size: 13px${important};
          line-height: 1.4${important};
        }
        .ttp-mobile-landing__error svg {
          flex-shrink: 0${important};
          margin-top: 1px${important};
          stroke: #dc2626${important};
        }

        .ttp-mobile-landing__actions {
          padding: 0 14px 14px${important};
          display: flex${important};
          gap: 10px${important};
        }

        .ttp-mobile-landing__btn {
          flex: 1${important};
          padding: 14px${important};
          border-radius: 12px${important};
          border: none${important};
          cursor: pointer${important};
          font-family: inherit${important};
          font-size: 14px${important};
          font-weight: 600${important};
          display: flex${important};
          align-items: center${important};
          justify-content: center${important};
          gap: 10px${important};
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1)${important};
          touch-action: manipulation${important};
          -webkit-tap-highlight-color: transparent${important};
        }

        .ttp-mobile-landing__btn svg {
          width: 20px${important};
          height: 20px${important};
          flex-shrink: 0${important};
        }

        .ttp-mobile-landing__btn--call {
          background: #fff${important};
          color: ${header.backgroundColor || '#7C3AED'};
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)${important};
        }

        .ttp-mobile-landing__btn--call:hover {
          transform: translateY(-2px)${important};
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2)${important};
        }

        .ttp-mobile-landing__btn--text {
          background: rgba(255, 255, 255, 0.15)${important};
          color: #fff${important};
          border: 1px solid rgba(255, 255, 255, 0.1)${important};
        }

        .ttp-mobile-landing__btn--text:hover {
          background: rgba(255, 255, 255, 0.2)${important};
          border-color: rgba(255, 255, 255, 0.2)${important};
        }

        .ttp-mobile-landing__footer {
          padding: 10px 16px${important};
          display: flex${important};
          justify-content: center${important};
          border-top: 1px solid rgba(255, 255, 255, 0.08)${important};
        }

        .ttp-mobile-landing__powered {
          color: rgba(255, 255, 255, 0.4)${important};
          font-size: 11px${important};
          display: flex${important};
          align-items: center${important};
          gap: 4px${important};
        }

        .ttp-mobile-landing__powered svg {
          width: 10px${important};
          height: 10px${important};
          color: #f59e0b${important};
        }

        .ttp-mobile-landing__powered a {
          color: ${footerLinkColor}${important};
          text-decoration: none${important};
        }

        .ttp-mobile-landing__powered a:hover {
          opacity: 0.88${important};
        }
      }
    `;
  }

  setupWidgetEvents() {
    if (!this.shadowRoot) return;
    
    // Setup prompt animation visibility and interactions
    this.setupPromptAnimation();
    
    // Desktop launcher: click toggles panel
    const launcherBtn = this.shadowRoot.getElementById('text-chat-button');
    if (launcherBtn) {
      launcherBtn.onclick = (e) => {
        e.stopPropagation();
        if (this.config.promptAnimation.hideAfterClick) {
          this.hidePrompt();
        }
        this.togglePanel();
      };
    }
    
    const closeBtn = this.shadowRoot.getElementById('closeBtn');
    if (closeBtn) {
      closeBtn.onclick = () => {
        this._doTogglePanel();
      };
    }
    
    // New dark-theme close buttons (idle and active states)
    const closeIdle = this.shadowRoot.getElementById('ttpCloseIdle');
    if (closeIdle) {
      closeIdle.onclick = () => this._doTogglePanel();
    }
    const closeActive = this.shadowRoot.getElementById('ttpCloseActive');
    if (closeActive) {
      closeActive.onclick = () => this._doTogglePanel();
    }
    
    // "Send a Message" button in idle view → switch to text interface
    const startTextBtn = this.shadowRoot.getElementById('startTextBtn');
    if (startTextBtn) {
      startTextBtn.onclick = () => this.showText();
    }

    const textUnifiedBackBtn = this.shadowRoot.getElementById('textUnifiedBackBtn');
    if (textUnifiedBackBtn) {
      textUnifiedBackBtn.onclick = () => {
        const widgetMode = this.config.behavior?.mode || 'unified';
        if (widgetMode !== 'unified') return;
        this.showLanding();
        this._openMobileCallTextLanding();
      };
    }
    
    // Build wave bars inside the orb (desktop active call)
    const waveQueryRoot = this.config.useShadowDOM
      ? this.shadowRoot
      : document.getElementById('ttp-widget-container');
    const waveBox = waveQueryRoot?.querySelector('#ttp-wave');
    if (waveBox && waveBox.children.length === 0) {
      [7, 14, 22, 18, 26, 18, 22, 14, 7].forEach((h, i) => {
        const bar = document.createElement('div');
        bar.className = 'ttp-wave-bar';
        bar.style.height = h + 'px';
        bar.style.animationDelay = (i * 0.07) + 's';
        waveBox.appendChild(bar);
      });
    }
    
    // Voice selection events (based on widget mode)
    const widgetMode = this.config.behavior?.mode || 'unified';
    const isUnified = widgetMode === 'unified';
    const showVoice = widgetMode === 'unified' || widgetMode === 'voice-only';
    const showText = widgetMode === 'unified' || widgetMode === 'text-only';
    
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    
    // Setup back button handler
    if (backBtn) {
      backBtn.onclick = () => {
        // Check if we're showing a domain error — clear it and return to voice hero
        if (this.voiceInterface?.isShowingDomainError) {
          console.log('🔙 Back button clicked - clearing domain error and returning to voice hero');
          // Clear the domain error flag
          this.voiceInterface.isShowingDomainError = false;
          // Reset the connecting state (this will restore normal UI)
          if (this.voiceInterface?.resetConnectingState) {
            this.voiceInterface.resetConnectingState();
          }
          this.showLanding();
          this._openMobileCallTextLanding();
          return;
        }
        
        if (this.voiceInterface?.isActive) {
          // If call is active, show message inside widget instead of modal
          this.showBackButtonWarning();
        } else if (window.innerWidth <= 768) {
          this.showLanding();
          this._openMobileCallTextLanding();
        } else {
          // Desktop: return to voice hero (unified home)
          this.showLanding();
        }
      };
    }
    
    if (isUnified) {
      if (this.currentView === 'text') {
        if (textInterface) textInterface.classList.add('active');
        if (voiceInterface) voiceInterface.classList.remove('active');
        if (backBtn) backBtn.classList.add('visible');
        if (newChatBtn) newChatBtn.style.display = '';
      } else {
        // Text-only path is text branch; everything else is unified home (voice hero)
        this.currentView = 'voice';
        if (textInterface) textInterface.classList.remove('active');
        if (voiceInterface) voiceInterface.classList.add('active');
        if (backBtn) backBtn.classList.remove('visible');
        if (newChatBtn) newChatBtn.style.display = 'none';
      }
    } else if (widgetMode === 'voice-only') {
      // Voice-only mode: show voice interface directly
      if (voiceInterface) voiceInterface.classList.add('active');
      if (textInterface) textInterface.classList.remove('active');
      // Back button doesn't exist in voice-only mode (only in unified)
    } else if (widgetMode === 'text-only') {
      // Text-only mode: show text interface directly
      if (textInterface) textInterface.classList.add('active');
      if (voiceInterface) voiceInterface.classList.remove('active');
      // Back button doesn't exist in text-only mode (only in unified)
    }
    
    // Setup interface event handlers
    if (showVoice) {
      console.log('⚙️ Setting up voice interface event handlers');
      this.voiceInterface.setupEventHandlers();
    }
    if (showText) {
      console.log('⚙️ Setting up text interface event handlers');
      this.textInterface.setupEventHandlers();
    }
    
    // Setup header button handlers
    if (newChatBtn) {
      newChatBtn.onclick = () => this.textInterface.startNewChat();
      // Hide new chat on unified home (voice hero) initially
      if (isUnified && this.currentView !== 'text') {
        newChatBtn.style.display = 'none';
      }
    }
    
    // Mobile floating button & landing overlay
    this.setupMobileWidgetEvents();

    // Keyboard navigation
    if (this.config.accessibility.keyboardNavigation) {
      this.setupKeyboardNavigation();
    }
  }

  startNewChat() {
    this.textInterface.startNewChat();
  }

  /**
   * Unified-mode "home": voice idle hero. Name kept for VoiceInterface callbacks.
   * Legacy two-card in-panel mode picker has been removed.
   */
  showLanding() {
    if (!this.shadowRoot) return;

    if (this._isDomainErrorVisible()) return;

    const widgetMode = this.config.behavior.mode || 'unified';
    if (widgetMode !== 'unified') return;

    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');

    this.currentView = 'voice';
    if (textInterface) textInterface.classList.remove('active');
    if (voiceInterface) voiceInterface.classList.add('active');
    if (backBtn) backBtn.classList.remove('visible');
    if (newChatBtn) newChatBtn.style.display = 'none';
  }

  showText() {
    if (!this.shadowRoot) return;
    this.currentView = 'text'; // Track state
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    if (voiceInterface) voiceInterface.classList.remove('active');
    this.textInterface.show();
    // Show back button when not on landing (only in unified mode)
    const widgetMode = this.config.behavior.mode || 'unified';
    if (backBtn && widgetMode === 'unified') backBtn.classList.add('visible');
    // Show new chat button when not on landing
    if (newChatBtn) newChatBtn.style.display = '';
  }

  _isDomainErrorVisible() {
    if (this.voiceInterface?.isShowingDomainError) return true;
    const idleState = this.voiceInterface?.shadowRoot?.getElementById('voiceIdleState');
    return idleState && idleState.dataset?.domainError === 'true';
  }

  showVoice() {
    if (!this.shadowRoot) return;
    this.currentView = 'voice'; // Track state
    const textInterface = this.shadowRoot.getElementById('textInterface');
    const voiceInterface = this.shadowRoot.getElementById('voiceInterface');
    const backBtn = this.shadowRoot.getElementById('backBtn');
    const newChatBtn = this.shadowRoot.getElementById('newChatBtn');
    if (textInterface) textInterface.classList.remove('active');
    if (voiceInterface) voiceInterface.classList.add('active');
    // Voice hero is unified home — no header back (text chat uses its own back bar)
    const widgetMode = this.config.behavior.mode || 'unified';
    if (backBtn && widgetMode === 'unified') backBtn.classList.remove('visible');
    // Hide new chat button on voice interface (not applicable)
    if (newChatBtn) newChatBtn.style.display = 'none';
  }

  /**
   * Mobile (≤768px): close chat panel and show Call vs Chat overlay (#ttpMobileLanding).
   * Hides FAB while landing is open. No-op on desktop or if mobile chrome is missing.
   */
  _openMobileCallTextLanding() {
    if (!this.shadowRoot || window.innerWidth > 768) return;
    const mobileFab = this.shadowRoot.querySelector('.ttp-mobile-fab');
    const mobileLanding = this.shadowRoot.getElementById('ttpMobileLanding');
    if (!mobileFab || !mobileLanding) return;

    // Defer showing landing until after the current pointer/click event finishes.
    // Otherwise document's "outside click" listener runs in the same bubble as e.g. the text
    // "Back" button: composedPath includes the panel but not #ttpMobileLanding, so it treated
    // the click as "outside" and closed the landing immediately (user only saw the FAB again).
    const open = () => {
      if (!this.shadowRoot || window.innerWidth > 768) return;
      const panel = this.shadowRoot.getElementById('text-chat-panel');
      if (panel) {
        panel.classList.remove('open');
        this.isOpen = false;
      }

      if (this.config.promptAnimation.hideAfterClick) {
        this.hidePrompt();
      }
      const errorBanner = this.shadowRoot.getElementById('ttpMobileLandingError');
      if (errorBanner) errorBanner.style.display = 'none';

      const fab = this.shadowRoot.querySelector('.ttp-mobile-fab');
      const landing = this.shadowRoot.getElementById('ttpMobileLanding');
      if (!fab || !landing) return;
      fab.style.display = 'none';
      landing.classList.add('active');
      this.isMobileLandingOpen = true;
    };
    queueMicrotask(open);
  }

  setupMobileWidgetEvents() {
    if (!this.shadowRoot) return;

    const mobileFab = this.shadowRoot.querySelector('.ttp-mobile-fab');
    const mobileLanding = this.shadowRoot.getElementById('ttpMobileLanding');
    if (!mobileFab || !mobileLanding) return;

    const closeMobileLanding = () => {
      mobileLanding.classList.remove('active');
      mobileFab.style.display = '';
      this.isMobileLandingOpen = false;
    };

    mobileFab.onclick = () => this._openMobileCallTextLanding();

    const mobileClose = mobileLanding.querySelector('.ttp-mobile-landing__close');
    if (mobileClose) {
      mobileClose.onclick = closeMobileLanding;
    }

    const mobileCallBtn = this.shadowRoot.getElementById('ttpMobileCallBtn');
    if (mobileCallBtn) {
      mobileCallBtn.onclick = () => {
        closeMobileLanding();
        const panel = this.shadowRoot.getElementById('text-chat-panel');
        if (panel) {
          panel.style.removeProperty('display');
          panel.classList.add('open');
          this.isOpen = true;
        }
        mobileFab.style.display = 'none';
        this.showVoice();
        // Landing already chose "Voice call" — start the call (mic permission / pre-prompt) instead of
        // leaving users on a second "Start Call" tap that feels broken.
        queueMicrotask(() => {
          this.startVoiceCall().catch((err) => {
            console.error('[TTPChatWidget] Mobile landing voice start failed:', err);
          });
        });
      };
    }

    const mobileChatBtn = this.shadowRoot.getElementById('ttpMobileChatBtn');
    if (mobileChatBtn) {
      mobileChatBtn.onclick = () => {
        closeMobileLanding();
        const panel = this.shadowRoot.getElementById('text-chat-panel');
        if (panel) {
          panel.style.removeProperty('display');
          panel.classList.add('open');
          this.isOpen = true;
        }
        mobileFab.style.display = 'none';
        this.showText();
      };
    }

    // Close mobile landing on outside click
    document.addEventListener('click', (e) => {
      if (!this.isMobileLandingOpen) return;
      const path = e.composedPath();
      if (!path.includes(mobileLanding) && !path.includes(mobileFab)) {
        closeMobileLanding();
      }
    });

    // Close mobile landing on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMobileLandingOpen) {
        closeMobileLanding();
      }
    });

    // Responsive breakpoint handler: keep desktop panel and mobile FAB/landing mutually exclusive
    let _prevIsMobile = window.innerWidth <= 768;
    let _resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(_resizeTimer);
      _resizeTimer = setTimeout(() => {
        const nowMobile = window.innerWidth <= 768;
        if (nowMobile === _prevIsMobile) return;
        _prevIsMobile = nowMobile;

        if (nowMobile) {
          // Switched desktop → mobile: close desktop panel so only mobile FAB shows
          if (this.isOpen) {
            const panel = this.shadowRoot?.getElementById('text-chat-panel');
            if (panel) {
              panel.classList.remove('open');
              this.isOpen = false;
            }
            const pill = this.shadowRoot?.getElementById('text-chat-button');
            if (pill) pill.classList.remove('open');
          }
        } else {
          // Switched mobile → desktop: close mobile landing/FAB so only desktop pill shows
          if (this.isMobileLandingOpen) {
            closeMobileLanding();
          }
          // Ensure mobile FAB is reset to CSS-controlled state (media query hides it on desktop)
          if (mobileFab) mobileFab.style.removeProperty('display');
        }
      }, 150);
    });
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.togglePanel();
      }
    });
  }

  /**
   * Setup prompt animation visibility and auto-hide timer
   */
  setupPromptAnimation() {
    if (!this.shadowRoot) return;
    
    const promptConfig = this.config.promptAnimation || {};
    // Default to disabled if not specified (enabled === true means explicitly enabled)
    const isPromptEnabled = promptConfig.enabled === true;
    
    console.log('🎨 setupPromptAnimation called:', {
      hasPromptConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      isOpen: this.isOpen
    });
    
    if (!isPromptEnabled) {
      // Hide prompt if disabled
      console.log('❌ Prompt disabled, hiding');
      this.hidePrompt();
      return;
    }
    
    // Show or hide prompt based on widget state
    // If widget is closed, show the prompt; if open, hide it
    if (this.isOpen) {
      this.hidePrompt();
    } else {
      this.showPrompt();
    }
    
    // Auto-hide after configured seconds (only if widget is closed)
    if (!this.isOpen && promptConfig.hideAfterSeconds !== null && promptConfig.hideAfterSeconds > 0) {
      this.promptAutoHideTimer = setTimeout(() => {
        this.hidePrompt();
      }, promptConfig.hideAfterSeconds * 1000);
    }
    
    // Hide pulse rings when voice call starts (listen to voice interface events)
    if (this.voiceInterface && this.voiceInterface.sdk) {
      // Store original onConversationStart if it exists
      const originalOnConversationStart = this.config.onConversationStart;
      
      // Wrap the callback to also hide pulse rings
      this.config.onConversationStart = (...args) => {
        // Hide pulse rings when call starts
        const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (pulseRings) pulseRings.style.display = 'none';
        
        // Call original callback if it exists
        if (originalOnConversationStart) {
          originalOnConversationStart(...args);
        }
      };
      
      // Also listen to recordingStarted event from SDK if available
      if (this.voiceInterface.sdk.voiceSDK) {
        this.voiceInterface.sdk.voiceSDK.on('recordingStarted', () => {
          const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
          if (pulseRings) pulseRings.style.display = 'none';
        });
      }
    }
  }

  /**
   * Hide the prompt bubble and pulse rings
   */
  hidePrompt() {
    if (!this.shadowRoot) return;
    
    // Clear auto-hide timer if it exists
    if (this.promptAutoHideTimer) {
      clearTimeout(this.promptAutoHideTimer);
      this.promptAutoHideTimer = null;
    }
    
    // Hide prompt bubble (check both shadow root and document for compatibility)
    let promptBubble = this.shadowRoot.getElementById('prompt-bubble');
    if (!promptBubble && this.shadowRoot !== document) {
      promptBubble = document.getElementById('prompt-bubble');
    }
    if (promptBubble) {
      promptBubble.style.display = 'none';
      promptBubble.style.visibility = 'hidden';
    }
    
    // Hide pulse rings (check both shadow root and document for compatibility)
    let pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
    if (!pulseRings && this.shadowRoot !== document) {
      pulseRings = document.getElementById('prompt-pulse-rings');
    }
    if (pulseRings) {
      pulseRings.style.display = 'none';
      pulseRings.style.visibility = 'hidden';
    }
  }

  /**
   * Show the prompt bubble and pulse rings (when widget is closed)
   */
  showPrompt() {
    if (!this.shadowRoot) return;
    
    const promptConfig = this.config.promptAnimation || {};
    // Default to enabled if not specified
    const isPromptEnabled = promptConfig.enabled !== false;
    
    console.log('👁️ showPrompt called:', {
      hasPromptConfig: !!this.config.promptAnimation,
      promptConfig: promptConfig,
      enabled: promptConfig.enabled,
      isPromptEnabled: isPromptEnabled,
      shadowRoot: !!this.shadowRoot
    });
    
    if (!isPromptEnabled) {
      console.log('❌ Prompt not enabled, skipping show');
      return; // Don't show if disabled
    }
    
    // Function to actually show the elements
    const doShow = () => {
      // Show prompt bubble (check both shadow root and document for compatibility)
      let promptBubble = this.shadowRoot.getElementById('prompt-bubble');
      if (!promptBubble && this.shadowRoot !== document) {
        promptBubble = document.getElementById('prompt-bubble');
      }
      if (promptBubble) {
        promptBubble.style.display = 'block';
        promptBubble.style.visibility = 'visible';
        promptBubble.style.opacity = '1';
        console.log('✅ Prompt bubble shown:', promptBubble);
      } else {
        console.warn('⚠️ Prompt bubble not found in shadowRoot or document');
      }
      
      // Show pulse rings if enabled
      if (promptConfig.showPulseRings !== false) {
        let pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (!pulseRings && this.shadowRoot !== document) {
          pulseRings = document.getElementById('prompt-pulse-rings');
        }
        if (pulseRings) {
          pulseRings.style.display = 'block';
          pulseRings.style.visibility = 'visible';
          pulseRings.style.opacity = '1';
        }
      }
    };
    
    // Try to show immediately
    doShow();
    
    // If elements don't exist yet, retry after delays (for widget initialization)
    const promptBubble = this.shadowRoot.getElementById('prompt-bubble');
    const pulseRings = promptConfig.showPulseRings !== false ? this.shadowRoot.getElementById('prompt-pulse-rings') : null;
    
    if (!promptBubble || (promptConfig.showPulseRings !== false && !pulseRings)) {
      // Retry after short delay
      setTimeout(() => {
        doShow();
      }, 100);
      
      // Retry after longer delay if still not found
      setTimeout(() => {
        doShow();
      }, 500);
    }
    
    // Restart auto-hide timer if configured
    if (promptConfig.hideAfterSeconds !== null && promptConfig.hideAfterSeconds > 0) {
      // Clear any existing timer
      if (this.promptAutoHideTimer) {
        clearTimeout(this.promptAutoHideTimer);
      }
      // Start new timer
      this.promptAutoHideTimer = setTimeout(() => {
        this.hidePrompt();
      }, promptConfig.hideAfterSeconds * 1000);
    }
  }

  togglePanel() {
    if (!this.shadowRoot) return;
    
    // Just toggle the panel - call continues regardless of open/close state
    // User can minimize/maximize widget without ending the call
    this._doTogglePanel();
  }
  
  _doTogglePanel() {
    if (!this.shadowRoot) return;
    this.isOpen = !this.isOpen;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel) {
      panel.classList.toggle('open');
      if (this.isOpen) {
        panel.style.removeProperty('display');
      }
    }
    
    // Toggle .open on pill launcher for icon animation
    const pill = this.shadowRoot.getElementById('text-chat-button');
    if (pill) {
      pill.classList.toggle('open', this.isOpen);
    }
    
    // Hide/show prompt animation based on panel state
    if (this.isOpen) {
      // Panel is opening - hide prompt
      this.hidePrompt();
      
      setTimeout(() => {
        const input = this.shadowRoot.getElementById('messageInput');
        if (input) input.focus();
      }, 100);
    } else {
      // Panel is closing — restore mobile FAB if on mobile
      if (window.innerWidth <= 768) {
        const fab = this.shadowRoot.querySelector('.ttp-mobile-fab');
        if (fab) fab.style.display = '';
      }
      // Panel is closing - show prompt (if enabled)
      this.showPrompt();
      setTimeout(() => {
        const promptBubble = this.shadowRoot.getElementById('prompt-bubble');
        if (promptBubble && promptBubble.style.display === 'none') {
          promptBubble.style.display = 'block';
        }
        const pulseRings = this.shadowRoot.getElementById('prompt-pulse-rings');
        if (pulseRings && this.config.promptAnimation?.showPulseRings !== false) {
          if (pulseRings.style.display === 'none') {
            pulseRings.style.display = 'block';
          }
        }
      }, 100);
    }
    
    // Auto-connect if enabled
    if (this.isOpen && !this.isActive && this.config.behavior.autoConnect) {
      this.startChat();
    }
  }
  
  /**
   * Show error toast/notification to user
   * @param {string} message - Error message to display
   * @param {string} type - Type of notification ('error', 'warning', 'info')
   */
  showErrorToast(message, type = 'error') {
    // Create toast element
    const toast = document.createElement('div');
    toast.className = `widget-toast widget-toast-${type}`;
    toast.textContent = message;
    
    // Style the toast
    const bgColor = type === 'error' ? '#ef4444' : type === 'warning' ? '#f59e0b' : '#3b82f6';
    Object.assign(toast.style, {
      position: 'fixed',
      bottom: '20px',
      left: '50%',
      transform: 'translateX(-50%)',
      backgroundColor: bgColor,
      color: 'white',
      padding: '12px 24px',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: '10003',
      fontSize: '14px',
      fontWeight: '500',
      maxWidth: '90%',
      textAlign: 'center',
      animation: 'slideUp 0.3s ease-out',
      fontFamily: 'inherit',
      pointerEvents: 'auto'
    });
    
    // Add CSS animation if not already added
    if (!document.getElementById('widget-toast-styles')) {
      const style = document.createElement('style');
      style.id = 'widget-toast-styles';
      style.textContent = `
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes slideDown {
          from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          to {
            opacity: 0;
            transform: translateX(-50%) translateY(20px);
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Add to shadow root or document
    const container = this.shadowRoot || document.body;
    container.appendChild(toast);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'slideDown 0.3s ease-out';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }
  
  showMobileLandingError(message) {
    if (!this.shadowRoot) return;
    const mobileLanding = this.shadowRoot.getElementById('ttpMobileLanding');
    const mobileFab = this.shadowRoot.querySelector('.ttp-mobile-fab');
    const errorBanner = this.shadowRoot.getElementById('ttpMobileLandingError');
    if (!mobileLanding || !errorBanner) return;

    // Set error text
    const errorText = errorBanner.querySelector('.ttp-mobile-landing__error-text');
    if (errorText) errorText.textContent = message;
    errorBanner.style.display = 'flex';

    // Show the mobile landing overlay with the error
    mobileLanding.classList.add('active');
    if (mobileFab) mobileFab.style.display = 'none';
    this.isMobileLandingOpen = true;
  }

  /**
   * Show confirmation modal (reusing modal pattern)
   * @param {Object} options - Modal configuration
   */
  showConfirmModal(options) {
    const {
      title = 'Confirm',
      message = 'Are you sure?',
      confirmText = 'Confirm',
      cancelText = 'Cancel',
      confirmButtonClass = 'primary',
      onConfirm,
      onCancel
    } = options;
    
    // Create overlay and modal (similar to MicPermissionModals pattern)
    const overlay = document.createElement('div');
    overlay.className = 'widget-modal-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 10000;
    `;
    
    const modal = document.createElement('div');
    modal.className = 'widget-confirm-modal';
    modal.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 24px;
      max-width: 400px;
      width: 90%;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    `;
    
    const confirmBtnClass = confirmButtonClass === 'danger' ? 'widget-modal-btn--danger' : 'widget-modal-btn--primary';
    modal.innerHTML = `
      <h3 style="margin: 0 0 12px 0; font-size: 18px; font-weight: 600; color: #1e293b;">${title}</h3>
      <p style="margin: 0 0 24px 0; color: #64748b; font-size: 14px; line-height: 1.5;">${message}</p>
      <div style="display: flex; gap: 12px; justify-content: flex-end;">
        <button class="widget-modal-btn widget-modal-btn--cancel" data-action="cancel" style="
          padding: 10px 20px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        ">
          ${cancelText}
        </button>
        <button class="widget-modal-btn ${confirmBtnClass}" data-action="confirm" style="
          padding: 10px 20px;
          border: none;
          background: ${confirmButtonClass === 'danger' ? '#ef4444' : '#7c3aed'};
          color: white;
          border-radius: 8px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        ">
          ${confirmText}
        </button>
      </div>
    `;
    
    overlay.appendChild(modal);
    const container = this.shadowRoot || document.body;
    container.appendChild(overlay);
    
    // Event handlers
    modal.querySelector('[data-action="confirm"]').onclick = () => {
      overlay.remove();
      onConfirm?.();
    };
    
    modal.querySelector('[data-action="cancel"]').onclick = () => {
      overlay.remove();
      onCancel?.();
    };
    
    overlay.onclick = (e) => {
      if (e.target === overlay) {
        overlay.remove();
        onCancel?.();
      }
    };
  }

  async startChat() {
    // No-op with single-shot design; connection happens per message
    this.isActive = true;
    this.textInterface.setActive(true);
  }

  async sendMessage() {
    await this.textInterface.sendMessage();
  }

  updateSendButtonState() {
    this.textInterface.updateSendButtonState();
  }

  /**
   * Show warning message inside widget when back button is clicked during active call
   */
  showBackButtonWarning() {
    if (!this.voiceInterface?.isActive) return;
    
    // Show warning message in the voice interface
    if (this.voiceInterface.desktop?.showBackButtonWarning) {
      this.voiceInterface.desktop.showBackButtonWarning();
    }
  }

  buildWebSocketUrl() {
    if (!this.config.agentId) {
      throw new Error('agentId is required');
    }
    
    const baseUrl = this.config.websocketUrl || DEFAULT_VOICE_WEBSOCKET_URL;
    const url = new URL(baseUrl, 'wss://placeholder.com');
    const params = url.searchParams;
    
    if (!params.has('agentId')) {
      params.append('agentId', this.config.agentId);
    }
    
    if (this.config.appId && !params.has('appId')) {
      params.append('appId', this.config.appId);
    }
    
    if (this.config.demo !== false) {
      params.append('demo', 'true');
    }
    
    const wsUrl = `${url.origin}${url.pathname}?${params.toString()}`;
    
    if (typeof __DEV__ !== 'undefined' && __DEV__) {
      console.log('[TTPAgentSDK] WebSocket URL:', wsUrl);
    }
    
    return wsUrl;
  }

  // Delegated to TextInterface
  addMessage(type, text) {
    this.textInterface.addMessage(type, text);
  }

  beginStreaming() {
    this.textInterface.beginStreaming();
  }

  appendStreamingChunk(chunk) {
    this.textInterface.appendStreamingChunk(chunk);
  }

  finalizeStreaming(fullText) {
    this.textInterface.finalizeStreaming(fullText);
  }

  stopStreamingState() {
    this.textInterface.stopStreamingState();
  }

  showError(message) {
    this.textInterface.showError(message);
  }

  updateStatus(status) {
    // Status update handler (can be overridden by users)
  }

  // Public API methods
  updateConfig(newConfig) {
    
    // Deep merge nested objects
    const mergedConfig = { ...this.config };
    
    // Deep merge panel config if it exists (common config)
    if (newConfig.panel) {
      mergedConfig.panel = { ...this.config.panel, ...newConfig.panel };
    }
    
    // Deep merge voice config if it exists (root `agentName` only — ignore `voice.agentName`)
    if (newConfig.voice) {
      const { agentName: _ignoredVoiceAgentName, ...voicePatch } = newConfig.voice;
      mergedConfig.voice = { ...this.config.voice, ...voicePatch };
      // Deep merge micButtonHint if it exists
      if (newConfig.voice.micButtonHint) {
        mergedConfig.voice.micButtonHint = {
          ...this.config.voice?.micButtonHint,
          ...newConfig.voice.micButtonHint
        };
      }
      delete mergedConfig.voice.agentName;
    }
    
    // Deep merge text config if it exists
    if (newConfig.text) {
      mergedConfig.text = { ...this.config.text, ...newConfig.text };
      // Deep merge sendButtonHint if it exists
      if (newConfig.text.sendButtonHint) {
        mergedConfig.text.sendButtonHint = {
          ...this.config.text?.sendButtonHint,
          ...newConfig.text.sendButtonHint
        };
      }
    }
    
    // Update language if provided - must be before mergeWithDefaults
    if (newConfig.language !== undefined) {
      mergedConfig.language = newConfig.language;
    }
    
    // Update direction if language changed to RTL language
    if (newConfig.language !== undefined) {
      const langBase = String(newConfig.language).toLowerCase().split(/[-_]/)[0];
      if (langBase === 'he' || langBase === 'ar') {
        mergedConfig.direction = 'rtl';
      } else if (newConfig.direction === undefined) {
        mergedConfig.direction = 'ltr';
      }
    }
    
    // Update translations if provided
    if (newConfig.translations) {
      mergedConfig.translations = newConfig.translations;
      this.translations = newConfig.translations;
    }
    
    // Merge other configs (shallow merge is fine for these)
    if (newConfig.button) {
      mergedConfig.button = { ...this.config.button, ...newConfig.button };
    }
    if (newConfig.header) {
      mergedConfig.header = { ...this.config.header, ...newConfig.header };
    }
    if (newConfig.icon) {
      mergedConfig.icon = { ...this.config.icon, ...newConfig.icon };
    }
    if (newConfig.messages) {
      mergedConfig.messages = { ...this.config.messages, ...newConfig.messages };
    }
    if (newConfig.animation) {
      mergedConfig.animation = { ...this.config.animation, ...newConfig.animation };
    }
    if (newConfig.behavior) {
      mergedConfig.behavior = { ...this.config.behavior, ...newConfig.behavior };
    }
    if (newConfig.accessibility) {
      mergedConfig.accessibility = { ...this.config.accessibility, ...newConfig.accessibility };
    }
    if (newConfig.tooltips) {
      mergedConfig.tooltips = { ...this.config.tooltips, ...newConfig.tooltips };
    }
    if (newConfig.landing) {
      mergedConfig.landing = { ...this.config.landing, ...newConfig.landing };
    }
    if (newConfig.footer) {
      mergedConfig.footer = { ...this.config.footer, ...newConfig.footer };
    }
    
    // Merge direction property (only if not set by language change above)
    if (newConfig.direction !== undefined && mergedConfig.direction === undefined) {
      mergedConfig.direction = newConfig.direction;
    }
    
    // Merge primaryColor if provided
    if (newConfig.primaryColor !== undefined) {
      mergedConfig.primaryColor = newConfig.primaryColor;
    }
    
    // Merge event callbacks - preserve existing hooks if not provided in newConfig
    mergedConfig.onConversationStart = newConfig.onConversationStart !== undefined 
      ? newConfig.onConversationStart 
      : this.config.onConversationStart;
    mergedConfig.onConversationEnd = newConfig.onConversationEnd !== undefined 
      ? newConfig.onConversationEnd 
      : this.config.onConversationEnd;
    mergedConfig.onBargeIn = newConfig.onBargeIn !== undefined 
      ? newConfig.onBargeIn 
      : this.config.onBargeIn;
    mergedConfig.onAudioStartPlaying = newConfig.onAudioStartPlaying !== undefined 
      ? newConfig.onAudioStartPlaying 
      : this.config.onAudioStartPlaying;
    mergedConfig.onAudioStoppedPlaying = newConfig.onAudioStoppedPlaying !== undefined 
      ? newConfig.onAudioStoppedPlaying 
      : this.config.onAudioStoppedPlaying;
    mergedConfig.onSubtitleDisplay = newConfig.onSubtitleDisplay !== undefined 
      ? newConfig.onSubtitleDisplay 
      : this.config.onSubtitleDisplay;
    mergedConfig.onVoiceCallButtonClick = newConfig.onVoiceCallButtonClick !== undefined
      ? newConfig.onVoiceCallButtonClick
      : this.config.onVoiceCallButtonClick;

    // Merge useShadowDOM if provided
    if (newConfig.useShadowDOM !== undefined) {
      mergedConfig.useShadowDOM = newConfig.useShadowDOM;
    }
    
    // Merge any other top-level properties
    Object.keys(newConfig).forEach(key => {
      if (!['panel', 'button', 'header', 'footer', 'icon', 'messages', 'direction', 'voice', 'text', 'animation', 'behavior', 'accessibility', 'language', 'tooltips', 'landing', 'primaryColor', 'useShadowDOM', 'onConversationStart', 'onConversationEnd', 'onBargeIn', 'onAudioStartPlaying', 'onAudioStoppedPlaying', 'onSubtitleDisplay', 'onVoiceCallButtonClick'].includes(key)) {
        mergedConfig[key] = newConfig[key];
      }
    });
    
    // Store current language before merge to detect changes
    const oldLanguage = this.config?.language || 'en';
    // Store current useShadowDOM setting
    const oldUseShadowDOM = this.config?.useShadowDOM !== false;
    
    this.config = this.mergeWithDefaults(mergedConfig);
    
    // Check if useShadowDOM changed - if so, need to recreate container
    const useShadowDOMChanged = (this.config.useShadowDOM !== false) !== oldUseShadowDOM;
    
    if (useShadowDOMChanged || !this.container) {
      // Remove existing containers
      const existingHost = document.getElementById('ttp-widget-shadow-host');
      if (existingHost) {
        existingHost.remove();
      }
      const existingContainer = document.getElementById('ttp-widget-container');
      if (existingContainer) {
        existingContainer.remove();
      }
      
      // Remove styles from head if switching away from non-Shadow DOM
      if (oldUseShadowDOM === false) {
        const existingStyle = document.getElementById('ttp-widget-styles');
        if (existingStyle) {
          existingStyle.remove();
        }
      }
      
      // Recreate container based on new setting
      if (this.config.useShadowDOM) {
        this.createShadowHost();
        this.container = this.shadowRoot;
      } else {
        this.createRegularContainer();
        this.container = this.regularContainer;
        this.shadowRoot = document;
      }
    } else {
      // Just clean up existing containers if they exist
      const existingHost = document.getElementById('ttp-widget-shadow-host');
      if (existingHost && !this.config.useShadowDOM) {
        existingHost.remove();
      }
      const existingContainer = document.getElementById('ttp-widget-container');
      if (existingContainer && this.config.useShadowDOM) {
        existingContainer.remove();
      }
      
      // Ensure container references are correct
      if (this.config.useShadowDOM) {
        if (!this.shadowRoot) {
          const host = document.getElementById('ttp-widget-shadow-host');
          if (host) {
            this.shadowRoot = host.shadowRoot;
            this.container = this.shadowRoot;
          } else {
            this.createShadowHost();
            this.container = this.shadowRoot;
          }
        } else {
          this.container = this.shadowRoot;
        }
      } else {
        if (!this.regularContainer) {
          const container = document.getElementById('ttp-widget-container');
          if (container) {
            this.regularContainer = container;
            this.container = container;
          } else {
            this.createRegularContainer();
            this.container = this.regularContainer;
          }
        } else {
          this.container = this.regularContainer;
        }
        this.shadowRoot = document;
      }
    }
    
    // Recreate interfaces with new config (after mergeWithDefaults, so language is current)
    // Ensure language is correctly passed - use the merged config value
    const currentLanguage = this.config.language || 'en';
    const desktopMinimizedEcommerceStripUi =
      typeof window !== 'undefined' &&
      window.innerWidth > 768 &&
      this.config.flavor?.callView === 'minimized';
    const voiceConfig = {
      ...this.config,
      ...this.config.voice,
      agentName: this.config.agentName,
      language: currentLanguage, // Use the current language from merged config
      translations: this.translations,
      shadowRoot: this.shadowRoot, // Pass shadow root reference
      websocketUrl: this.config.websocketUrl,
      agentSettingsOverride: this.config.agentSettingsOverride,
      visualAssistant: this.config.visualAssistant,
      inputFormat: this.config.inputFormat,
      // Output format defaults for widget: 24 kHz, 16-bit, PCM, raw
      outputContainer: this.config.voice?.outputContainer || 'raw',
      outputEncoding: this.config.voice?.outputEncoding || 'pcm',
      outputSampleRate: this.config.voice?.outputSampleRate || 24000,
      outputChannels: this.config.voice?.outputChannels || 1,
      outputBitDepth: this.config.voice?.outputBitDepth || 16,
      desktopMinimizedEcommerceStripUi,
      onDesktopMinimizedStripCallStart: desktopMinimizedEcommerceStripUi
        ? () => {
            this._ecommerceVoiceStrip?.show();
            if (this.isOpen) this.minimize();
            this._setPanelFooterVisible(false);
            this._setEcommerceStripLauncherVisible(false);
          }
        : undefined,
      onDesktopMinimizedStripLauncherRestore: desktopMinimizedEcommerceStripUi
        ? () => this._restoreDesktopEcommerceStripChrome()
        : undefined,
      // Pass event callbacks to voice interface (forward only; panel is not auto-minimized on call start)
      onConversationStart: (...args) => {
        if (typeof this.config.onConversationStart === 'function') {
          this.config.onConversationStart(...args);
        }
      },
      onConversationEnd: this.config.onConversationEnd,
      onBargeIn: this.config.onBargeIn,
      onAudioStartPlaying: this.config.onAudioStartPlaying,
      onAudioStoppedPlaying: this.config.onAudioStoppedPlaying,
      onSubtitleDisplay: this.config.onSubtitleDisplay,
      onCallEnd: () => {
        if (this._isDomainErrorVisible()) return;
        const widgetMode = this.config.behavior?.mode || 'unified';
        if (widgetMode === 'unified') {
          if (window.innerWidth <= 768) {
            const panel = this.shadowRoot?.getElementById('text-chat-panel');
            if (panel) {
              panel.classList.remove('open');
              this.isOpen = false;
            }
            const fab = this.shadowRoot?.querySelector('.ttp-mobile-fab');
            if (fab) fab.style.display = '';
          }
          this.showLanding();
        }
      },
      onShowVoice: () => {
        this.showVoice();
      },
      onShowLanding: () => {
        this.showLanding();
      },
      onDisclaimerDeclined: () => {
        if (this._isDomainErrorVisible()) return;
        const widgetMode = this.config.behavior?.mode || 'unified';
        if (widgetMode === 'voice-only') {
          this.showVoice();
          return;
        }
        if (widgetMode !== 'unified') return;
        if (window.innerWidth <= 768) {
          const panel = this.shadowRoot?.getElementById('text-chat-panel');
          if (panel) {
            panel.classList.remove('open');
            this.isOpen = false;
          }
          this._openMobileCallTextLanding();
        } else {
          this.showLanding();
        }
      },
      direction: this.getEffectiveTextDirection(),
    };
    this.voiceInterface = new VoiceInterface(voiceConfig);

    // Re-wire universal widget-control listener since voiceInterface (and its
    // AgentSDK + voiceSDK) is fresh — see constructor for full rationale.
    this._wireWidgetControlListeners();

    const textConfig = {
      ...this.config,
      ...this.config.text,
      language: currentLanguage, // Ensure language is set from merged config
      translations: this.translations,
      shadowRoot: this.shadowRoot // Pass shadow root reference
    };
    this.textInterface = new TextInterface(textConfig, this.sdk);
    this.textInterface._widget = this; // Reference for gallery viewer in inline media
    
    // Recreate widget with new config (after interfaces are created)
    this.createWidget();
    
    // Update input attributes in case widget was recreated
    if (this.textInterface && this.textInterface.updateInputAttributes) {
      this.textInterface.updateInputAttributes();
    }

    // Recreated VoiceInterface = new AgentSDK; re-wire ecommerce bottom strip to onConversationStart / transcript.
    this._flavorObj = this.config.flavor && typeof this.config.flavor === 'object' ? this.config.flavor : null;
    if (this._ecommerceVoiceStrip) {
      this._ecommerceVoiceStrip.destroy();
      this._ecommerceVoiceStrip = null;
    }
    this._initEcommerceVoiceStrip();
  }

  /**
   * Initialize the bottom-strip voice UI for desktop + flavor.callView:"minimized".
   *
   * Activates when flavor.callView === "minimized" and Desktop (window.innerWidth > 768).
   *
   * The strip renders as a fixed bar at the bottom of the page and replaces the
   * in-panel voice active state during a call. Landing and idle views are unchanged.
   */
  _initEcommerceVoiceStrip() {
    const flavor = this.config.flavor && typeof this.config.flavor === 'object' ? this.config.flavor : null;
    if (window.innerWidth <= 768) return;
    if (!flavor || flavor.callView !== 'minimized') return;

    if (this._ecommerceVoiceStrip) {
      this._ecommerceVoiceStrip.destroy();
      this._ecommerceVoiceStrip = null;
    }

    const v = this.config.voice || {};
    let strip;
    strip = new EcommerceVoiceStrip({
      language: this.config.language || 'en',
      direction: this.getEffectiveTextDirection(),
      footer: this.getFooterBranding(),
      theme: {
        pillGradient: v.pillGradient || 'linear-gradient(135deg, #581c87, #312e81, #1e1b4b)',
        pillTextColor: v.pillTextColor || '#ffffff',
        pillDotColor: v.pillDotColor || '#4ade80',
        endCallButtonColor: v.endCallButtonColor || '#ef4444',
      },
      getVoiceUiState: () => ({
        isMicMuted: !!this.voiceInterface?.isMicMuted,
        isPaused: !!this.voiceInterface?.isPaused,
        isSpeakerMuted: !!this.voiceInterface?.isSpeakerMuted,
      }),
      onHangup: () => {
        if (this.voiceInterface) this.voiceInterface.endVoiceCall();
      },
      onToggleMute: () => {
        if (this.voiceInterface) this.voiceInterface.toggleMute();
        strip.syncFromVoice();
      },
      onTogglePause: () => {
        if (this.voiceInterface) this.voiceInterface.togglePause();
        strip.syncFromVoice();
      },
      onToggleSpeaker: () => {
        if (this.voiceInterface) this.voiceInterface.toggleSpeaker();
        strip.syncFromVoice();
      },
      onSendText: (text) => {
        const voiceSDK = this.voiceInterface?.sdk?.voiceSDK;
        if (voiceSDK && typeof voiceSDK.sendMessage === 'function') {
          voiceSDK.sendMessage({ t: 'text_inject', text });
        }
      },
    });

    this._ecommerceVoiceStrip = strip;

    // Show strip when conversation starts
    const agentSDK = this.voiceInterface?.sdk;
    if (agentSDK) {
      const origConvStart = agentSDK.onConversationStart;
      agentSDK.onConversationStart = (...args) => {
        strip.show({ reset: false });
        strip.syncFromVoice();
        if (typeof origConvStart === 'function') origConvStart.call(agentSDK, ...args);
      };

      // Feed transcript updates into the strip
      const origOnTranscript = agentSDK.onTranscript;
      agentSDK.onTranscript = (text) => {
        strip.updateTranscript(text);
        if (typeof origOnTranscript === 'function') origOnTranscript.call(agentSDK, text);
      };

      const origOnUserTranscript = agentSDK.onUserTranscript;
      agentSDK.onUserTranscript = (utext, isFinal) => {
        try {
          strip.updateUserTranscript(utext, isFinal);
        } catch (e) {
          console.warn('[TTPChatWidget] ecommerce strip user transcript:', e);
        }
        if (typeof origOnUserTranscript === 'function') {
          origOnUserTranscript.call(agentSDK, utext, isFinal);
        }
      };
    }

    // Hide strip when call ends (config.onCallEnd is called by endVoiceCall())
    if (this.voiceInterface) {
      const origOnCallEnd = this.voiceInterface.config.onCallEnd;
      this.voiceInterface.config.onCallEnd = () => {
        this._restoreDesktopEcommerceStripChrome();
        if (typeof origOnCallEnd === 'function') origOnCallEnd();
      };

      // Feed timer ticks into the strip
      const origUpdateTimer = this.voiceInterface._updateTimerElements?.bind(this.voiceInterface);
      if (origUpdateTimer) {
        this.voiceInterface._updateTimerElements = (timerText, elapsed) => {
          strip.updateTimer(timerText);
          origUpdateTimer(timerText, elapsed);
        };
      }
    }
  }


  destroy() {
    // Clear prompt animation timer if it exists
    if (this.promptAutoHideTimer) {
      clearTimeout(this.promptAutoHideTimer);
      this.promptAutoHideTimer = null;
    }
    
    // Remove shadow host if it exists
    const host = document.getElementById('ttp-widget-shadow-host');
    if (host) {
      host.remove();
    }
    // Remove regular container if it exists
    const container = document.getElementById('ttp-widget-container');
    if (container) {
      container.remove();
    }
    // Remove styles from head if they exist
    const style = document.getElementById('ttp-widget-styles');
    if (style) {
      style.remove();
    }
    if (this.sdk) {
      this.sdk.destroy();
    }
    // Clean up interface resources
    if (this.voiceInterface) {
      this.voiceInterface.destroy();
    }
    // Clean up flavor
    if (this._flavor && this._flavor.destroy) {
      this._flavor.destroy();
      this._flavor = null;
    }
    // Clean up ecommerce voice strip
    if (this._ecommerceVoiceStrip) {
      this._ecommerceVoiceStrip.destroy();
      this._ecommerceVoiceStrip = null;
    }
  }

  // Delegated to VoiceInterface
  async startVoiceCall() {
    // Note: isStartingCall is managed by the caller (voice card handler)
    // This method just delegates to voiceInterface
    await this.voiceInterface.startVoiceCall();
  }

  endVoiceCall() {
    this.voiceInterface.endVoiceCall();
  }

  toggleMute() {
    this.voiceInterface.toggleMute();
  }

  toggleSpeaker() {
    this.voiceInterface.toggleSpeaker();
  }

  /**
   * Inject data/text into the active voice conversation programmatically.
   * This method delegates to the underlying VoiceSDK and returns a Promise.
   * 
   * @param {string} data - The data/text to inject
   * @param {Object} options - Optional configuration
   * @param {string} options.source - Source identifier (default: 'widget_programmatic')
   * @param {Object} options.metadata - Optional metadata (will be echoed back in ack)
   * @param {number} options.timeout - Timeout in milliseconds (default: 10000)
   * @returns {Promise<Object>} Promise resolving to acknowledgment message on success
   * 
   * @example
   * const widget = new TTPChatWidget({ ... });
   * 
   * // Simple usage
   * try {
   *   const ack = await widget.injectData('User ID: 12345');
   *   console.log('Data injected:', ack);
   * } catch (error) {
   *   console.error('Failed:', error);
   * }
   * 
   * // With metadata
   * const ack = await widget.injectData('Form submitted', {
   *   metadata: { formId: 'contact' }
   * });
   */
  async injectData(data, options = {}) {
    // Validation: data content
    if (!data || !data.trim()) {
      console.warn('TTPChatWidget: Cannot inject empty data');
      throw new Error('Cannot inject empty data');
    }

    // Check if voice interface is active
    if (!this.voiceInterface || !this.voiceInterface.isActive) {
      console.warn('TTPChatWidget: Cannot inject data - voice call is not active');
      throw new Error('Voice call is not active');
    }

    // Check if SDK is available
    const voiceSDK = this.voiceInterface?.getVoiceSDK();
    if (!voiceSDK) {
      console.warn('TTPChatWidget: VoiceSDK not available');
      throw new Error('VoiceSDK not available');
    }

    // Delegate to SDK method (which handles correlation IDs and Promise resolution)
    // Widget just passes through options with default source
    try {
      const ack = await voiceSDK.injectData(data, {
        ...options,
        source: options.source || 'widget_programmatic'
      });
      return ack;
    } catch (error) {
      // Re-throw error (SDK already logged it)
      throw error;
    }
  }

  /**
   * Register a handler for a client tool
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @example
   * widget.registerToolHandler('getPageContent', async (params) => {
   *   return { title: document.title, url: window.location.href };
   * });
   */
  registerToolHandler(name, handler) {
    if (typeof handler !== 'function') {
      throw new Error(`Handler for tool '${name}' must be a function`);
    }
    this._sharedToolHandlers.set(name, handler);
    return this;
  }

  /**
   * Unregister a tool handler
   * @param {string} name - Tool name
   */
  unregisterToolHandler(name) {
    this._sharedToolHandlers.delete(name);
    return this;
  }

  /**
   * Check if a tool handler is registered
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasToolHandler(name) {
    return this._sharedToolHandlers.has(name);
  }

  /**
   * Get all registered tool handler names
   * @returns {string[]}
   */
  getRegisteredTools() {
    return Array.from(this._sharedToolHandlers.keys());
  }

  /**
   * Register a client tool handler (alias for registerToolHandler)
   * @param {string} name - Tool name (must match database definition)
   * @param {Function} handler - Async function that receives parameters and returns result
   * @returns {this} For method chaining
   * @example
   * widget.registerClientTool('getLocation', async (params) => {
   *   const pos = await new Promise((resolve, reject) => {
   *     navigator.geolocation.getCurrentPosition(resolve, reject, {
   *       enableHighAccuracy: params.highAccuracy
   *     });
   *   });
   *   return { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
   * });
   */
  registerClientTool(name, handler) {
    return this.registerToolHandler(name, handler);
  }

  /**
   * Unregister a client tool handler (alias for unregisterToolHandler)
   * @param {string} name - Tool name to unregister
   * @returns {this}
   */
  unregisterClientTool(name) {
    return this.unregisterToolHandler(name);
  }

  /**
   * Check if a client tool is registered (alias for hasToolHandler)
   * @param {string} name - Tool name
   * @returns {boolean}
   */
  hasClientTool(name) {
    return this.hasToolHandler(name);
  }

  /**
   * Get list of registered client tool names (alias for getRegisteredTools)
   * @returns {string[]}
   */
  getRegisteredClientTools() {
    return this.getRegisteredTools();
  }
}

